import React, { memo, useCallback, useEffect, useMemo, useRef, useState, forwardRef, useImperativeHandle } from 'react';
import axios from 'axios';
import {
  Autocomplete,
  Badge,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  IconButton,
  InputAdornment,
  Menu,
  MenuItem,
  Paper,
  Popover,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import SelectAllIcon from '@mui/icons-material/SelectAll';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import FilterListIcon from '@mui/icons-material/FilterList';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import CloseIcon from '@mui/icons-material/Close';
import ViewColumnIcon from '@mui/icons-material/ViewColumn';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import config from '../../config/config';
import CollapsibleTable from '../../components/table';
import { useSession } from '../../components/hooks/useSession';
import handleOpenSweetAlert from '../../components/handleOpenSweetAlert';
import { fetchUserPrefs, saveUserPrefs } from '../../utils/userPrefs';
import { getStatusMobileTheme } from '../../components/Table/utils/formatters';

const CHECK_MARK = '\u2713';
const CONCEPT_PREVIEW_LENGTH = 180;
const CONCEPT_PREVIEW_LINES = 3;
const TAB_LISTAS = 'listas';
const TAB_PENDIENTES = 'pendientes';
const TAB_TODAS = 'todas';
const STORAGE_KEY_COLUMNS = 'reporteAutorizadas_columnasVisibles';
const STORAGE_KEY_COLUMN_ORDER = 'reporteAutorizadas_ordenColumnas';
const STORAGE_KEY_ALL_STATUS = 'reporteAutorizadas_verTodosEstatus';
const STORAGE_KEY_ROWS_PER_PAGE = 'reporteAutorizadas_rowsPerPage';

const ALL_COLUMNS_DEF = [
  { id: 'orden', label: 'ORD / OC', defaultVisible: true, alwaysVisible: true },
  { id: 'empresa', label: 'EMPRESA', defaultVisible: true },
  { id: 'pago', label: 'PAGO SOLICITADO', defaultVisible: true },
  { id: 'moneda', label: 'MONEDA', defaultVisible: true },
  { id: 'totalMn', label: 'TOTAL MN', defaultVisible: false },
  { id: 'totalUsd', label: 'TOTAL USD', defaultVisible: false },
  { id: 'totalEur', label: 'TOTAL EUR', defaultVisible: false },
  { id: 'fechaCompromiso', label: 'FECHA COMPROMISO', defaultVisible: true },
  { id: 'concepto', label: 'DESCRIPCIÓN', defaultVisible: true },
  { id: 'economico', label: 'ECONÓMICO', defaultVisible: true },
  { id: 'centroCosto', label: 'CENTRO COSTO', defaultVisible: true },
  { id: 'proveedor', label: 'PROVEEDOR', defaultVisible: true },
  { id: 'departamento', label: 'DEPARTAMENTO', defaultVisible: false },
  { id: 'fechaSolicitud', label: 'FECHA SOLICITUD', defaultVisible: false },
  { id: 'fechaAutorizacion', label: 'FECHA AUTORIZACIÓN', defaultVisible: false },
  { id: 'fechaReportePago', label: 'FECHA REPORTE PAGO', defaultVisible: false },
  { id: 'diasSinPago', label: 'DÍAS S/PAGO', defaultVisible: false },
  { id: 'tipoPago', label: 'TIPO DE PAGO', defaultVisible: false },
  { id: 'exhibit', label: 'EXHIBIT', defaultVisible: false },
  { id: 'comprobante', label: 'COMPROBANTE', defaultVisible: false },
  { id: 'accion', label: 'ACCIÓN / ESTATUS', defaultVisible: true, alwaysVisible: true },
];
const REPORTE_STATUS_REQUESTS = [
  { id: 3, label: 'Autorizada' },
  { id: 1, label: 'Default' },
  { id: 2, label: 'Proceso' },
  { id: 11, label: 'Vencida' },
];
const REPORTE_STATUS_IDS = new Set(REPORTE_STATUS_REQUESTS.map(status => status.id));
const REPORTE_STATUS_BY_TEXT = {
  default: 1,
  proceso: 2,
  pendiente: 2,
  autorizada: 3,
  vencida: 11,
};
const REPORTE_STATUS_LABELS = {
  1: 'Default',
  2: 'Proceso',
  3: 'Autorizada',
  11: 'Vencida',
};
const REPORTE_STATUS_HELP = {
  1: 'Aun no inicia el flujo de autorizacion.',
  2: 'La solicitud sigue en proceso de autorizacion.',
  11: 'La solicitud esta vencida y requiere revision antes de pago.',
};

const parseMoney = (value) => {
  if (value == null || value === '') return 0;
  if (typeof value === 'number') return Number.isFinite(value) ? value : 0;
  const parsed = Number(String(value).replace(/[^0-9.-]/g, ''));
  return Number.isFinite(parsed) ? parsed : 0;
};

const applyThinBorder = (cell, color = 'D9E2F3') => {
  cell.border = {
    top: { style: 'thin', color: { argb: color } },
    left: { style: 'thin', color: { argb: color } },
    bottom: { style: 'thin', color: { argb: color } },
    right: { style: 'thin', color: { argb: color } },
  };
};

const formatCurrency = (value, currency) => {
  const safeCurrency = currency === 'USD' ? 'USD' : 'MXN';
  return new Intl.NumberFormat('es-MX', {
    style: 'currency',
    currency: safeCurrency,
  }).format(value || 0);
};

const getFechaOrden = (row) => {
  const raw =
    row.FECHA_AUTORIZACION ||
    row.FECHA_AUT_N3 ||
    row.FECHA_AUT_N2 ||
    row.FECHA_AUT_N1 ||
    row.FECHASOLICITUD ||
    row.FECHA_SOLICITUD ||
    row.FECHA_ORDENCOMPRA ||
    row.FECHA ||
    row.VENCIMIENTO ||
    '';
  const time = new Date(raw).getTime();
  return Number.isFinite(time) ? time : 0;
};

const formatInputDate = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const getCurrentYearRange = () => {
  const today = new Date();
  return {
    start: `${today.getFullYear()}-01-01`,
    end: formatInputDate(new Date(today.getFullYear(), today.getMonth() + 1, 0)),
  };
};

const normalizeText = (value) => String(value ?? '').toLowerCase().trim();

const getReporteStatusId = (row = {}) => {
  const rawId = row.ESTATUSID ?? row.ID_ESTATUS ?? row.ESTATUS_ID ?? row.estatusId;
  const numericId = Number(rawId);
  if (Number.isInteger(numericId) && numericId > 0) return numericId;
  return REPORTE_STATUS_BY_TEXT[normalizeText(row.ESTATUS)] || 0;
};

const getReporteStatusLabel = (statusId) => REPORTE_STATUS_LABELS[statusId] || 'Sin estatus';

const getReporteStatusHelp = (statusId) => REPORTE_STATUS_HELP[statusId] || 'La solicitud aun no esta lista para exportarse a pago.';

const getUniqueOptions = (values) => (
  Array.from(new Set(values.map(value => String(value ?? '').trim()).filter(Boolean)))
    .sort((a, b) => a.localeCompare(b, 'es'))
);

const getCentroCostoNumero = (row = {}) => (
  String(row.NUMEROCC || row.NUMERO_CENTRO_COSTO || row.NUMERO_CC || '').trim()
);

const getCentroCostoNombre = (row = {}) => (
  String(row.CENTROCOSTO || row.CENTRO_COSTO || row.NOMBRE_CC || '').trim()
);

const formatCentroCostoOption = (numero = '', nombre = '') => {
  const safeNumero = String(numero || '').trim();
  const safeNombre = String(nombre || '').trim();
  if (safeNumero && safeNombre && safeNumero.toUpperCase() !== safeNombre.toUpperCase()) {
    return `${safeNumero} - ${safeNombre}`;
  }
  return safeNumero || safeNombre;
};

const splitCentroCostoOption = (value = '') => (
  String(value)
    .split(' - ')
    .map(part => part.trim())
    .filter(Boolean)
);

const getCentroCostoFilterValue = (value = '') => {
  const parts = splitCentroCostoOption(value);
  return parts.length > 1 ? parts[parts.length - 1] : String(value).trim();
};

const getCentroCostoFilterValues = (values = []) => (
  values
    .map(getCentroCostoFilterValue)
    .filter(Boolean)
);

const getSelectedSummary = (values = []) => {
  if (!values.length) return '';
  if (values.length === 1) {
    const first = values[0];
    return typeof first === 'string' ? first : first?.nombre || String(first);
  }
  return `${values.length} seleccionadas`;
};

const renderSummaryInput = (params, label, selectedValues = []) => {
  const summary = getSelectedSummary(selectedValues);
  const isSelected = Boolean(summary);

  return (
    <TextField
      {...params}
      placeholder={summary ? undefined : label}
      InputProps={{
        ...params.InputProps,
        startAdornment: summary ? (
          <Box
            component="span"
            sx={{
              maxWidth: 'calc(100% - 24px)',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              fontSize: '0.68rem',
              fontWeight: 700,
              color: '#0655A5',
            }}
          >
            {summary}
          </Box>
        ) : params.InputProps.startAdornment,
      }}
      sx={{
        ...(isSelected ? {
          '& .MuiInputBase-root': {
            bgcolor: '#f0f7ff !important',
            borderColor: '#93c5fd !important',
            '& fieldset': {
              borderColor: '#60a5fa !important',
              borderWidth: '1.5px !important',
            }
          }
        } : {})
      }}
    />
  );
};

const icon = <CheckBoxOutlineBlankIcon fontSize="small" sx={{ fontSize: 16, color: '#94a3b8' }} />;
const checkedIcon = <CheckBoxIcon fontSize="small" sx={{ fontSize: 16, color: '#0655A5' }} />;

const renderCheckboxOption = (props, option, { selected }) => (
  <li
    {...props}
    style={{
      padding: '4px 8px',
      fontSize: '0.74rem',
      fontWeight: selected ? 700 : 500,
      backgroundColor: selected ? '#e0f2fe' : 'transparent',
      color: selected ? '#0369a1' : '#1e293b',
      display: 'flex',
      alignItems: 'center',
      gap: 6,
      borderBottom: '1px solid #f8fafc',
    }}
  >
    <Checkbox
      icon={icon}
      checkedIcon={checkedIcon}
      style={{ marginRight: 4, padding: 0 }}
      checked={selected}
      size="small"
    />
    <Box component="span" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 }}>
      {typeof option === 'string' ? option : option?.nombre || ''}
    </Box>
  </li>
);

const compactAutocompleteSx = {
  '& .MuiInputBase-root': {
    height: 28,
    minHeight: 28,
    maxHeight: 28,
    borderRadius: '10px !important',
    bgcolor: '#ffffff',
    fontSize: '0.68rem',
    fontWeight: 500,
    px: '8px !important',
    py: '0px !important',
    flexWrap: 'nowrap',
    overflow: 'hidden',
    display: 'flex',
    alignItems: 'center',
    '& fieldset': { 
      borderColor: '#cbd5e1 !important',
      top: 0,
      '& legend': { display: 'none' }
    },
    '&:hover fieldset': { borderColor: '#94a3b8 !important' },
    '&.Mui-focused fieldset': { borderColor: '#0655A5 !important', borderWidth: '1.5px !important' },
    '& .MuiAutocomplete-input': {
      minWidth: '20px !important',
      py: '0px !important',
      fontSize: '0.68rem',
      color: '#0f172a',
      '&::placeholder': {
        color: '#64748b',
        opacity: 1,
        fontWeight: 500,
      }
    },
    '& .MuiAutocomplete-endAdornment': {
      position: 'absolute',
      right: '6px',
      top: '50%',
      transform: 'translateY(-50%)',
      display: 'flex',
      alignItems: 'center',
      gap: 0,
      '& .MuiAutocomplete-clearIndicator': {
        display: 'none',
      },
      '& .MuiIconButton-root': {
        p: '2px',
        color: '#64748b',
        '&:hover': { color: '#0655A5', bgcolor: 'transparent' },
        '& .MuiSvgIcon-root': { fontSize: '15px' }
      }
    }
  },
  '& .MuiInputLabel-root': {
    display: 'none',
  },
};

const hasActiveContentFilters = (filterData = {}) => (
  Boolean(String(filterData.BUSQUEDA_RAPIDA || '').trim()) ||
  (Array.isArray(filterData.PROYECTOS) && filterData.PROYECTOS.length > 0) ||
  (Array.isArray(filterData.DIVISIONES) && filterData.DIVISIONES.length > 0) ||
  (Array.isArray(filterData.DEPARTAMENTOS) && filterData.DEPARTAMENTOS.length > 0) ||
  (Array.isArray(filterData.CENTRO_COSTOS) && filterData.CENTRO_COSTOS.length > 0) ||
  (Array.isArray(filterData.PROVEEDORES) && filterData.PROVEEDORES.length > 0) ||
  (Array.isArray(filterData.DIVISAS) && filterData.DIVISAS.length > 0)
);

const normalizeEmpresaArray = (value) => {
  if (value == null) return [];
  if (Array.isArray(value)) return value.map(Number).filter(id => Number.isInteger(id) && id > 0);
  const text = String(value).trim();
  if (!text) return [];
  if (text.startsWith('[') && text.endsWith(']')) {
    try {
      const parsed = JSON.parse(text);
      return Array.isArray(parsed) ? parsed.map(Number).filter(id => Number.isInteger(id) && id > 0) : [];
    } catch {
      return [];
    }
  }
  return text
    .split(',')
    .map(id => Number(id.trim()))
    .filter(id => Number.isInteger(id) && id > 0);
};

const getReporteKey = (row) => {
  const autsolpagoid = row.ID || row.AUTSOLPAGOID || row.autsolpagoid || '';
  if (autsolpagoid) return `AUT:${autsolpagoid}`;

  const empresaId = row.empresaId || row.EMPRESAID || row.IDEMPRESA || '';
  const folioOc = row.orden || row.FOLIO_OC || row.FOLIO || '';
  return empresaId && folioOc ? `${empresaId}|${folioOc}` : '';
};

const buildHistorialItem = (row) => ({
  autsolpagoid: row.ID || row.AUTSOLPAGOID || null,
  empresaId: row.empresaId || row.EMPRESAID || row.IDEMPRESA || null,
  empresa: row.empresa || row.EMPRESA || '',
  folioOc: row.orden || row.FOLIO_OC || row.FOLIO || '',
  proveedor: row.proveedor || row.PROVEEDOR || '',
  monto: row.pago ?? row.MONTO_SOLICITADO ?? row.MONTOSOLICITADO ?? 0,
  divisa: row.moneda || row.DIVISA || row.MONEDA || '',
  centroCosto: row.centroCosto || row.CENTROCOSTO || row.CENTRO_COSTO || row.NUMERO_CENTRO_COSTO || '',
  departamento: row.departamento || row.DEPARTAMENTO || row.NOMBRE_DEPARTAMENTO || '',
  fechaSolicitud: row.FECHASOLICITUD || row.FECHA_SOLICITUD || row.FECHA || '',
});

const getConcepto = (row) => {
  const partidas = Array.isArray(row.partidas) ? row.partidas : [];
  if (partidas.length) {
    return partidas
      .map((p) => {
        const desc = p.DESCRIPCION_PART || p.DESCRIPCION || 'S/D';
        const cantidad = p.CANTIDAD ? ` (Ct: ${p.CANTIDAD})` : '';
        return `* ${desc}${cantidad}`;
      })
      .join('\n');
  }
  return row.CONCEPTOPAGO || row.CONCEPTO || row.OBSERVACIONES || '';
};

const getInfoCompromiso = (fechaCompromisoRaw) => {
  if (!fechaCompromisoRaw) return { fechaStr: '', label: '', dias: null, nivel: 'sin_fecha' };
  
  const str = String(fechaCompromisoRaw).split('T')[0];
  const parts = str.split('-');
  if (parts.length !== 3) return { fechaStr: String(fechaCompromisoRaw), label: '', dias: null, nivel: 'sin_fecha' };
  
  const ano = Number(parts[0]);
  const mes = Number(parts[1]) - 1;
  const dia = Number(parts[2]);

  const fechaComp = new Date(ano, mes, dia);
  const hoy = new Date();
  hoy.setHours(0, 0, 0, 0);

  const diffTime = fechaComp.getTime() - hoy.getTime();
  const diffDays = Math.round(diffTime / (1000 * 3600 * 24));

  const fechaFormateada = `${String(dia).padStart(2, '0')}/${String(mes + 1).padStart(2, '0')}/${ano}`;

  if (diffDays < 0) {
    return {
      fechaStr: fechaFormateada,
      dias: diffDays,
      label: `Vencido ${Math.abs(diffDays)}d`,
      nivel: 'rojo',
    };
  } else if (diffDays === 0) {
    return {
      fechaStr: fechaFormateada,
      dias: 0,
      label: 'Vence hoy',
      nivel: 'rojo',
    };
  } else if (diffDays === 1) {
    return {
      fechaStr: fechaFormateada,
      dias: 1,
      label: 'Vence mañana',
      nivel: 'rojo',
    };
  } else if (diffDays <= 5) {
    return {
      fechaStr: fechaFormateada,
      dias: diffDays,
      label: `Pagar en ${diffDays}d`,
      nivel: 'naranja',
    };
  } else {
    return {
      fechaStr: fechaFormateada,
      dias: diffDays,
      label: `Pagar en ${diffDays}d`,
      nivel: 'verde',
    };
  }
};

const getDescripcionText = (row) => {
  const partidas = Array.isArray(row.partidas) ? row.partidas : [];
  if (partidas.length) {
    const list = partidas
      .map(p => p.DESCRIPCION_PART || p.DESCRIPCION || '')
      .filter(Boolean);
    if (list.length) return list.join('\n');
  }
  return row.DESCRIPCION || row.DESCRIPCION_OC || row.DESCRIPCION_PART || row.CONCEPTOPAGO || row.CONCEPTO || row.OBSERVACIONES || '';
};

const calcDiasRetraso = (fechaStr) => {
  if (!fechaStr) return '';
  const text = String(fechaStr).trim();
  let date;
  const slashMatch = text.match(/^(\d{2})\/(\d{2})\/(\d{4})/);
  if (slashMatch) {
    date = new Date(Number(slashMatch[3]), Number(slashMatch[2]) - 1, Number(slashMatch[1]));
  } else {
    date = new Date(text.split(/[T\s]/)[0]);
  }
  if (!date || isNaN(date.getTime())) return '';
  date.setHours(0, 0, 0, 0);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const diff = Math.max(0, Math.floor((today.getTime() - date.getTime()) / 86400000));
  return `${diff}d`;
};

const prepareRow = (row) => {
  const fechaAut = row.FECHA_AUTORIZACION_UNIFICADA || row.FECHA_AUTORIZACION || row.FECHA_AUT_ULTIMA || row.FECHA_AUT_N3 || row.FECHA_AUT_N2 || row.FECHA_AUT_N1 || '';
  const fechaRep = row.FECHA_REPORTE_PAGO || row.FECHA_REPORTE || row.FECHA_SOLICITUD_PAGO || '';
  const diasSinP = row.DIAS_SIN_PAGO || calcDiasRetraso(fechaRep || fechaAut);

  return {
    ...row,
    estatusId: getReporteStatusId(row),
    estatusLabel: getReporteStatusLabel(getReporteStatusId(row)),
    estatusHelp: getReporteStatusHelp(getReporteStatusId(row)),
    empresaId: row.EMPRESAID || row.IDEMPRESA || row.empresaId || row.EMPRESA_ID || '',
    orden: row.FOLIO_OC || row.FOLIO || row.ORDEN || '',
    empresa: row.EMPRESA || '',
    departamento: row.DEPARTAMENTO || row.NOMBRE_DEPARTAMENTO || '',
    moneda: (row.DIVISA || row.MONEDA || 'MXN').toString().toUpperCase(),
    pago: parseMoney(row.MONTOSOLICITADO ?? row.MONTO_SOLICITADO ?? row.PAGO ?? row.TOTAL ?? 0),
    proveedor: row.PROVEEDOR || 'SIN PROVEEDOR',
    centroCostoNumero: getCentroCostoNumero(row),
    centroCostoNombre: getCentroCostoNombre(row),
    centroCosto: formatCentroCostoOption(getCentroCostoNumero(row), getCentroCostoNombre(row)),
    ID_PROYECTO: Number(row.ID_PROYECTO || row.IDPROYECTO || 0),
    PROYECTO: row.PROYECTO || row.NOMBRE_PROYECTO || '',
    ID_DIVISION: Number(row.ID_DIVISION || row.IDDIVISION || 0),
    DIVISION: row.DIVISION || row.NOMBRE_DIVISION || '',
    concepto: getConcepto(row),
    descripcion: getDescripcionText(row),
    exhibit: row.EXHIBICION || row.exhibicion || '',
    comprobante: row.COMPROBANTE || row.FOLIO_COMPROBANTE || row.REFERENCIA || (row.HAS_COMPROBANTE ? 'SI' : ''),
    tipoPago: row.TIPOPAGO || row.TIPO_PAGO || row.PAGO || '',
    comprador: row.COMPRADOR || row.COMPRADORA || row.NOMBRE_COMPRADOR || row.COMPRADOR_ERP || (row.REQUISITOR || row.NOMBRE_REQUISITOR ? (row.SOLICITANTE || '') : ''),
    requisitor: row.REQUISITOR || row.NOMBRE_REQUISITOR || row.SOLICITANTE_REQUI || row.SOLICITA || row.USUARIOSOLICITA || (!row.COMPRADOR && !row.NOMBRE_COMPRADOR ? (row.SOLICITANTE || '') : ''),
    fechaAutorizacion: fechaAut,
    fechaReportePago: fechaRep,
    diasSinPago: diasSinP,
    fechaSort: getFechaOrden(row),
    infoCompromiso: getInfoCompromiso(row.FECHA_COMPROMISO),
  };
};

const ConceptCell = memo(({ value }) => {
  const [expanded, setExpanded] = useState(false);
  const text = value || '';
  const lines = text.split('\n');
  const isLong = text.length > CONCEPT_PREVIEW_LENGTH || lines.length > CONCEPT_PREVIEW_LINES;

  const preview = useMemo(() => {
    if (!isLong || expanded) return text;
    const linePreview = lines.slice(0, CONCEPT_PREVIEW_LINES).join('\n');
    return linePreview.length > CONCEPT_PREVIEW_LENGTH
      ? `${linePreview.slice(0, CONCEPT_PREVIEW_LENGTH)}...`
      : `${linePreview}...`;
  }, [expanded, isLong, lines, text]);

  return (
    <Box sx={{ maxWidth: 420 }}>
      <Typography
        variant="body2"
        sx={{
          whiteSpace: 'pre-line',
          fontSize: 12,
          lineHeight: 1.35,
        }}
      >
        {preview}
      </Typography>
      {isLong && (
        <Button
          size="small"
          onClick={(e) => {
            e.stopPropagation();
            setExpanded(prev => !prev);
          }}
          sx={{ minWidth: 0, p: 0, mt: 0.25, textTransform: 'none', fontWeight: 700 }}
        >
          {expanded ? 'Ver menos' : 'Ver mas'}
        </Button>
      )}
    </Box>
  );
});

// ── POPOVER AISLADO: no re-renderiza la tabla completa cuando se abre/cierra ──
const TotalesPopover = forwardRef(function TotalesPopover({ totalesRef }, ref) {
  const [anchor, setAnchor] = useState(null);

  useImperativeHandle(ref, () => ({
    open: (el) => {
      setAnchor(el);
    },
    close: () => {
      setAnchor(null);
    }
  }), []);

  const breakdown = totalesRef?.current || {};

  return (
    <Popover
      open={Boolean(anchor)}
      anchorEl={anchor}
      onClose={() => setAnchor(null)}
      anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      transformOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      slotProps={{
        paper: {
          sx: {
            p: 1.5,
            borderRadius: '12px',
            boxShadow: '0 10px 25px -5px rgba(0,0,0,0.18), 0 8px 10px -6px rgba(0,0,0,0.1)',
            border: '1px solid #cbd5e1',
            minWidth: 260,
            bgcolor: '#ffffff',
          }
        }
      }}
    >
      <Box sx={{ mb: 1, pb: 0.5, borderBottom: '1px solid #f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Typography sx={{ fontSize: 11, fontWeight: 900, color: '#0f172a', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
          Detalle Total por Divisa
        </Typography>
        <Chip label="Totales" size="small" sx={{ height: 16, fontSize: 8.5, fontWeight: 800, bgcolor: '#e0f2fe', color: '#0369a1' }} />
      </Box>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.8 }}>
        {Object.entries(breakdown).map(([divisa, vals]) => {
          const colorMain =
            divisa === 'USD' ? '#b45309' :
            divisa === 'EUR' ? '#4338ca' :
            divisa === 'MXN' ? '#0369a1' : '#374151';
          const bgBadge =
            divisa === 'USD' ? '#fef3c7' :
            divisa === 'EUR' ? '#e0e7ff' :
            divisa === 'MXN' ? '#e0f2fe' : '#f3f4f6';

          return (
            <Box
              key={divisa}
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 1.5,
                p: 0.8,
                borderRadius: '8px',
                bgcolor: '#f8fafc',
                border: '1px solid #f1f5f9',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Chip
                  label={divisa}
                  size="small"
                  sx={{
                    height: 18,
                    fontSize: 9.5,
                    fontWeight: 900,
                    color: colorMain,
                    bgcolor: bgBadge,
                    '& .MuiChip-label': { px: 0.8 },
                  }}
                />
                <Typography sx={{ fontSize: 9.5, fontWeight: 700, color: '#64748b' }}>
                  ({vals.count || 0} OCs)
                </Typography>
              </Box>

              <Typography sx={{ fontSize: 12, fontWeight: 900, color: '#0f172a', fontVariantNumeric: 'tabular-nums' }}>
                ${(vals.total || 0).toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </Typography>
            </Box>
          );
        })}
      </Box>
    </Popover>
  );
});

const formatRequestDate = (rawDate) => {
  if (!rawDate) return '';
  const dateObj = new Date(rawDate);
  if (isNaN(dateObj.getTime())) return '';
  const day = String(dateObj.getUTCDate()).padStart(2, '0');
  const month = String(dateObj.getUTCMonth() + 1).padStart(2, '0');
  const year = dateObj.getUTCFullYear();
  return `${day}/${month}/${year}`;
};

const ReporteRow = memo(({
  row,
  autorizar,
  onAutorizar,
  selectable = true,
  visibleCols = {},
  columnOrder = [],
}) => {
  const historial = row.reporteHistorial;
  const veces = Number(historial?.veces || 0);
  const historialTitle = veces > 0
    ? historial.eventos?.map(evento => `${evento.fecha} - ${evento.usuario}`).join('\n')
    : '';

  const renderCellByKey = (colId) => {
    switch (colId) {
      case 'orden':
        return (
          <TableCell key="orden" sx={{ fontWeight: 800 }}>
            <Stack spacing={0.35}>
              <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap">
                <span>{row.orden}</span>
                {veces > 0 && (
                  <Tooltip title={<span style={{ whiteSpace: 'pre-line' }}>{historialTitle}</span>}>
                    <Chip
                      size="small"
                      color={veces >= 2 ? 'warning' : 'default'}
                      variant={veces >= 2 ? 'filled' : 'outlined'}
                      label={`Solicitada ${veces} ${veces === 1 ? 'vez' : 'veces'}`}
                      sx={{
                        height: 17,
                        fontSize: 9,
                        fontWeight: 700,
                        '& .MuiChip-label': {
                          px: 0.6,
                        },
                      }}
                    />
                  </Tooltip>
                )}
              </Stack>
            </Stack>
          </TableCell>
        );

      case 'empresa':
        return (
          <TableCell key="empresa" sx={{ fontWeight: 800 }}>
            <Stack spacing={0.25}>
              <span>{row.empresa}</span>
              <span style={{ fontSize: '11px', color: '#64748b', fontWeight: 500 }}>
                {formatRequestDate(row.FECHASOLICITUD)}
              </span>
            </Stack>
          </TableCell>
        );

      case 'pago':
        return (
          <TableCell key="pago" align="right" sx={{ fontWeight: 800 }}>
            {formatCurrency(row.pago, row.moneda)}
          </TableCell>
        );

      case 'moneda':
        return (
          <TableCell key="moneda" sx={{ fontWeight: 800 }}>
            {row.moneda}
          </TableCell>
        );

      case 'totalMn': {
        const isMxn = !row.moneda || row.moneda === 'MXN' || row.moneda === 'PESOS';
        return (
          <TableCell key="totalMn" align="right" sx={{ fontWeight: 800, color: isMxn ? '#0f172a' : '#94a3b8' }}>
            {isMxn ? formatCurrency(row.pago, 'MXN') : '-'}
          </TableCell>
        );
      }

      case 'totalUsd': {
        const isUsd = row.moneda === 'USD' || row.moneda === 'DOLARES' || row.moneda === 'DOLAR';
        return (
          <TableCell key="totalUsd" align="right" sx={{ fontWeight: 800, color: isUsd ? '#0284c7' : '#94a3b8' }}>
            {isUsd ? `$${Number(row.pago || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : '-'}
          </TableCell>
        );
      }

      case 'totalEur': {
        const isEur = row.moneda === 'EUR' || row.moneda === 'EUROS' || row.moneda === 'EURO';
        return (
          <TableCell key="totalEur" align="right" sx={{ fontWeight: 800, color: isEur ? '#7c3aed' : '#94a3b8' }}>
            {isEur ? `€${Number(row.pago || 0).toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : '-'}
          </TableCell>
        );
      }

      case 'fechaCompromiso': {
        const info = row.infoCompromiso;
        if (!info || info.nivel === 'sin_fecha') {
          return (
            <TableCell key="fechaCompromiso" align="center" sx={{ color: '#94a3b8' }}>
              -
            </TableCell>
          );
        }

        return (
          <TableCell key="fechaCompromiso" align="center">
            <Stack spacing={0.3} alignItems="center">
              <span style={{ fontSize: '11px', color: '#0f172a', fontWeight: 700 }}>
                {info.fechaStr}
              </span>
              <Chip
                size="small"
                label={info.label}
                color={
                  info.nivel === 'rojo' ? 'error' :
                  info.nivel === 'naranja' ? 'warning' : 'success'
                }
                sx={{
                  height: 18,
                  fontSize: 9.5,
                  fontWeight: 900,
                  '& .MuiChip-label': { px: 0.6 },
                }}
              />
            </Stack>
          </TableCell>
        );
      }

      case 'concepto':
        return (
          <TableCell key="concepto">
            <ConceptCell value={row.concepto} />
          </TableCell>
        );

      case 'economico':
        return (
          <TableCell key="economico">
            {row.NUMERO_ECONOMICO || '-'}
          </TableCell>
        );

      case 'centroCosto':
        return (
          <TableCell key="centroCosto">
            {row.centroCosto || '-'}
          </TableCell>
        );

      case 'proveedor':
        return (
          <TableCell key="proveedor" sx={{ fontWeight: 700 }}>
            {row.proveedor}
          </TableCell>
        );

      case 'departamento':
        return (
          <TableCell key="departamento">
            {row.departamento || '-'}
          </TableCell>
        );

      case 'fechaSolicitud':
        return (
          <TableCell key="fechaSolicitud">
            {formatRequestDate(row.FECHASOLICITUD)}
          </TableCell>
        );

      case 'fechaAutorizacion':
        return (
          <TableCell key="fechaAutorizacion">
            {row.fechaAutorizacion ? formatRequestDate(row.fechaAutorizacion) : '-'}
          </TableCell>
        );

      case 'fechaReportePago':
        return (
          <TableCell key="fechaReportePago">
            {row.fechaReportePago ? formatRequestDate(row.fechaReportePago) : '-'}
          </TableCell>
        );

      case 'diasSinPago':
        return (
          <TableCell key="diasSinPago" align="center">
            {row.diasSinPago ? (
              <Chip
                size="small"
                label={row.diasSinPago}
                color="warning"
                variant="outlined"
                sx={{ height: 20, fontSize: 10, fontWeight: 800 }}
              />
            ) : '-'}
          </TableCell>
        );

      case 'tipoPago':
        return (
          <TableCell key="tipoPago">
            {row.tipoPago || '-'}
          </TableCell>
        );

      case 'exhibit': {
        const cleanExhibitVal = row.exhibit ? String(row.exhibit).replace(/^Solicitud\s*/i, '').trim() : '';
        return (
          <TableCell key="exhibit" align="center">
            {cleanExhibitVal && cleanExhibitVal !== '-' && cleanExhibitVal !== '0' ? (
              <Chip
                label={cleanExhibitVal}
                size="small"
                sx={{
                  height: 20,
                  fontSize: 10,
                  fontWeight: 800,
                  bgcolor: '#f1f5f9',
                  color: '#0369a1',
                  border: '1px solid #bae6fd',
                  borderRadius: '6px',
                  px: 0.4,
                  '& .MuiChip-label': { px: 0.5 },
                }}
              />
            ) : (
              <Typography sx={{ fontSize: 11, color: '#94a3b8', fontWeight: 600 }}>
                -
              </Typography>
            )}
          </TableCell>
        );
      }

      case 'comprobante':
        return (
          <TableCell key="comprobante">
            {row.comprobante || '-'}
          </TableCell>
        );

      case 'accion':
        return !selectable ? (
          <TableCell key="accion" align="center">
            <Tooltip title={row.estatusHelp}>
              <Chip
                size="small"
                label={row.estatusLabel}
                color={row.estatusId === 11 ? 'error' : row.estatusId === 2 ? 'warning' : 'default'}
                variant={row.estatusId === 1 ? 'outlined' : 'filled'}
                sx={{
                  height: 22,
                  fontSize: 10,
                  fontWeight: 800,
                  '& .MuiChip-label': { px: 0.8 },
                }}
              />
            </Tooltip>
          </TableCell>
        ) : (
          <TableCell key="accion" align="center">
            <Checkbox checked={autorizar} onChange={() => onAutorizar(row.ID)} color="primary" />
          </TableCell>
        );

      default:
        return null;
    }
  };

  const effectiveOrder = columnOrder.length ? columnOrder : ALL_COLUMNS_DEF.map(c => c.id);

  return (
    <TableRow
      hover
      selected={selectable && autorizar}
      sx={!selectable ? {
        '& .MuiTableCell-body': {
          backgroundColor: '#f8fafc',
          color: '#475569',
        },
        '&:hover .MuiTableCell-body': {
          backgroundColor: '#f1f5f9',
          color: '#475569',
        },
      } : undefined}
    >
      {effectiveOrder.map(colId => {
        if (visibleCols[colId] === false) return null;
        return renderCellByKey(colId);
      })}
    </TableRow>
  );
});

const ReporteMobileCard = memo(({
  row,
  autorizar,
  onAutorizar,
  selectable = true,
  isReadyTab = true,
}) => {
  const [expandedConcepto, setExpandedConcepto] = useState(false);

  // Status label and theme calculation
  const statusLabel = row.estatusLabel || (isReadyTab ? 'Autorizada' : 'Pendiente');
  const theme = getStatusMobileTheme(statusLabel, null, 0);

  const historial = row.reporteHistorial;
  const veces = Number(historial?.veces || 0);
  const historialTitle = veces > 0
    ? historial.eventos?.map(evento => `${evento.fecha} - ${evento.usuario}`).join('\n')
    : '';

  const infoComp = row.infoCompromiso;
  const fechaSol = row.FECHASOLICITUD ? formatRequestDate(row.FECHASOLICITUD) : '';

  // Concepto preview
  const conceptoText = row.concepto || '';
  const lines = conceptoText.split('\n');
  const isLongConcepto = conceptoText.length > 140 || lines.length > 3;
  const conceptoPreview = isLongConcepto && !expandedConcepto
    ? `${lines.slice(0, 3).join('\n').slice(0, 140)}...`
    : conceptoText;

  return (
    <Card
      sx={{
        mb: 1.2,
        background: theme.gradient,
        borderRadius: '14px',
        border: `1px solid ${theme.border}`,
        borderLeft: `5px solid ${theme.primary}`,
        boxShadow: '0 2px 6px rgba(15, 23, 42, 0.05)',
        transition: 'all 0.15s ease-in-out',
        '&:active': { transform: 'scale(0.995)' },
      }}
    >
      <CardContent sx={{ p: 1.2, '&:last-child': { pb: 1.2 } }}>
        {/* Encabezado: Orden/OC + Solicitada X veces + Empresa + Checkbox / Estatus Chip */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 0.8 }}>
          <Box sx={{ flex: 1, minWidth: 0, pr: 0.8 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8, flexWrap: 'wrap' }}>
              <Typography sx={{ fontWeight: 900, color: '#0f172a', fontSize: '0.92rem', letterSpacing: '-0.01em' }}>
                {row.orden || '-'}
              </Typography>
              {veces > 0 && (
                <Tooltip title={<span style={{ whiteSpace: 'pre-line' }}>{historialTitle}</span>}>
                  <Chip
                    size="small"
                    color={veces >= 2 ? 'warning' : 'default'}
                    variant="filled"
                    label={`Solicitada ${veces} ${veces === 1 ? 'vez' : 'veces'}`}
                    sx={{
                      height: 18,
                      fontSize: '0.62rem',
                      fontWeight: 800,
                      bgcolor: veces >= 2 ? '#fef08a' : '#e2e8f0',
                      color: veces >= 2 ? '#854d0e' : '#1e293b',
                      '& .MuiChip-label': { px: 0.6 }
                    }}
                  />
                </Tooltip>
              )}
              {!selectable && (
                <Chip
                  size="small"
                  label={row.estatusLabel || 'Pendiente'}
                  color={row.estatusId === 11 ? 'error' : row.estatusId === 2 ? 'warning' : 'default'}
                  variant={row.estatusId === 1 ? 'outlined' : 'filled'}
                  sx={{ height: 20, fontSize: '0.62rem', fontWeight: 800, textTransform: 'uppercase' }}
                />
              )}
            </Box>
            {row.empresa && (
              <Typography sx={{ color: '#0284c7', fontWeight: 750, fontSize: '0.72rem', mt: 0.2 }}>
                {row.empresa} {fechaSol ? `• ${fechaSol}` : ''}
              </Typography>
            )}
          </Box>

          {selectable && (
            <Checkbox
              checked={autorizar}
              onChange={() => onAutorizar(row.ID)}
              color="primary"
              sx={{ p: 0.2, '& .MuiSvgIcon-root': { fontSize: 24 } }}
            />
          )}
        </Box>

        {/* Franja Destacada: Pago Solicitado + Moneda & Fecha Compromiso */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            bgcolor: 'rgba(255, 255, 255, 0.88)',
            backdropFilter: 'blur(4px)',
            px: 1.2,
            py: 0.6,
            borderRadius: '10px',
            border: `1px solid ${theme.border}`,
            mb: 0.8,
            boxShadow: '0 1px 3px rgba(15, 23, 42, 0.02)',
          }}
        >
          <Box>
            <Typography sx={{ fontSize: '0.58rem', fontWeight: 800, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.02em' }}>
              Pago Solicitado
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6 }}>
              <Typography sx={{ fontSize: '0.92rem', fontWeight: 900, color: '#0f172a' }}>
                {formatCurrency(row.pago, row.moneda)}
              </Typography>
              <Chip
                label={row.moneda || 'MXN'}
                size="small"
                sx={{
                  height: 18,
                  fontSize: '0.62rem',
                  fontWeight: 850,
                  bgcolor: row.moneda === 'USD' ? '#fef3c7' : row.moneda === 'EUR' ? '#e0e7ff' : '#e0f2fe',
                  color: row.moneda === 'USD' ? '#b45309' : row.moneda === 'EUR' ? '#4338ca' : '#0369a1',
                  borderRadius: '4px'
                }}
              />
            </Box>
          </Box>

          {infoComp && infoComp.nivel !== 'sin_fecha' && (
            <Box sx={{ textAlign: 'right', display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 0.2 }}>
              <Typography sx={{ fontSize: '0.58rem', fontWeight: 800, color: '#64748b', textTransform: 'uppercase' }}>
                Compromiso
              </Typography>
              <Chip
                size="small"
                label={`${infoComp.fechaStr} • ${infoComp.label}`}
                color={
                  infoComp.nivel === 'rojo' ? 'error' :
                  infoComp.nivel === 'naranja' ? 'warning' : 'success'
                }
                sx={{
                  height: 18,
                  fontSize: '0.62rem',
                  fontWeight: 850,
                  '& .MuiChip-label': { px: 0.6 }
                }}
              />
            </Box>
          )}
        </Box>

        {/* Datos Principales: Proveedor, Centro de Costo y Económico */}
        <Box sx={{ display: 'grid', gridTemplateColumns: '1.2fr 1fr', gap: '6px 12px', mt: 0.6 }}>
          {/* Proveedor */}
          <Box sx={{ minWidth: 0, gridColumn: '1 / -1' }}>
            <Typography sx={{ fontSize: '0.58rem', fontWeight: 800, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.01em' }}>
              Proveedor
            </Typography>
            <Typography sx={{ fontSize: '0.74rem', fontWeight: 750, color: '#0f172a', lineHeight: 1.25, mt: 0.15, wordBreak: 'break-word', textTransform: 'uppercase' }}>
              {row.proveedor || '-'}
            </Typography>
          </Box>

          {/* Centro de Costo */}
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ fontSize: '0.58rem', fontWeight: 800, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.01em' }}>
              Centro de Costo
            </Typography>
            <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: '#1e293b', lineHeight: 1.25, mt: 0.15, wordBreak: 'break-word', textTransform: 'uppercase' }}>
              {row.centroCosto || '-'}
            </Typography>
          </Box>

          {/* Económico */}
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ fontSize: '0.58rem', fontWeight: 800, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.01em' }}>
              Económico
            </Typography>
            <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: '#1e293b', lineHeight: 1.25, mt: 0.15, wordBreak: 'break-word', textTransform: 'uppercase' }}>
              {row.NUMERO_ECONOMICO || '-'}
            </Typography>
          </Box>
        </Box>

        {/* Descripción (directamente visible sin campos vacíos innecesarios) */}
        {conceptoText && (
          <Box sx={{ mt: 0.8, p: 0.8, bgcolor: 'rgba(255, 255, 255, 0.90)', borderRadius: '8px', border: `1px solid ${theme.border}` }}>
            <Typography sx={{ fontSize: '0.58rem', fontWeight: 800, color: '#64748b', textTransform: 'uppercase', mb: 0.2 }}>
              Descripción
            </Typography>
            <Typography sx={{ fontSize: '0.72rem', color: '#0f172a', lineHeight: 1.35, fontWeight: 600, whiteSpace: 'pre-line' }}>
              {conceptoPreview}
            </Typography>
            {isLongConcepto && (
              <Typography
                component="span"
                onClick={() => setExpandedConcepto(!expandedConcepto)}
                sx={{ fontSize: '0.64rem', fontWeight: 800, color: '#0655A5', cursor: 'pointer', display: 'inline-block', mt: 0.4 }}
              >
                {expandedConcepto ? 'Ver menos' : 'Ver más'}
              </Typography>
            )}
          </Box>
        )}
      </CardContent>
    </Card>
  );
});

const ReporteMobileTotalsFooter = memo(({
  preparedRows,
  selectedRows = [],
  selectedCount = 0,
  onGenerarExcel,
  isReadyTab,
  onSelectAll,
  allSelected,
}) => {
  const [expanded, setExpanded] = useState(false);
  if (!preparedRows || preparedRows.length === 0) return null;

  const calculateBreakdown = (rows = []) => {
    const breakdown = {};
    rows.forEach(r => {
      const val = parseMoney(r.pago);
      const rawDiv = String(r.moneda || 'MXN').toUpperCase().trim();
      const divisa = (rawDiv === 'USD' || rawDiv === 'DOLARES' || rawDiv === 'DOLAR') ? 'USD'
        : (rawDiv === 'EUR' || rawDiv === 'EUROS' || rawDiv === 'EURO') ? 'EUR'
        : 'MXN';

      if (!breakdown[divisa]) breakdown[divisa] = { total: 0, count: 0 };
      breakdown[divisa].total += val;
      breakdown[divisa].count += 1;
    });

    return Object.entries(breakdown).sort((a, b) => {
      const order = { MXN: 1, USD: 2, EUR: 3 };
      return (order[a[0]] || 99) - (order[b[0]] || 99);
    });
  };

  const totalEntries = calculateBreakdown(preparedRows);
  const selectedEntries = calculateBreakdown(selectedRows);
  const hasSelection = selectedCount > 0 && selectedEntries.length > 0;
  const displayEntries = hasSelection ? selectedEntries : totalEntries;

  const fmt = (n) => (n || 0).toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  return (
    <Box
      sx={{
        position: 'fixed',
        bottom: 0,
        left: 0,
        right: 0,
        zIndex: 1100,
        bgcolor: '#ffffff',
        borderTop: hasSelection ? '2px solid #2563eb' : '2px solid #0655A5',
        boxShadow: '0 -4px 20px rgba(15, 23, 42, 0.15)',
        px: 1.2,
        py: 0.8,
        display: { xs: 'block', md: 'none' },
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 0.8 }}>
        {/* Toggle de Selección o Conteo */}
        {isReadyTab ? (
          <Button
            size="small"
            variant="outlined"
            onClick={onSelectAll}
            sx={{
              height: 28,
              fontSize: '0.64rem',
              fontWeight: 800,
              textTransform: 'none',
              borderRadius: '8px',
              color: allSelected ? '#15803d' : hasSelection ? '#2563eb' : '#0655A5',
              borderColor: allSelected ? '#86efac' : hasSelection ? '#93c5fd' : '#cbd5e1',
              bgcolor: allSelected ? '#f0fdf4' : hasSelection ? '#eff6ff' : '#f8fafc',
              px: 0.8,
              flexShrink: 0,
            }}
          >
            {allSelected ? 'Desmarcar' : hasSelection ? `Selec. (${selectedCount})` : 'Selec. todas'}
          </Button>
        ) : (
          <Typography sx={{ fontSize: '0.70rem', fontWeight: 800, color: '#0f172a', flexShrink: 0 }}>
            {preparedRows.length} órdenes
          </Typography>
        )}

        {/* Resumen Compacto de Divisas (muestra seleccionadas si hay selección) */}
        <Box
          onClick={() => setExpanded(prev => !prev)}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
            cursor: 'pointer',
            overflowX: 'auto',
            scrollbarWidth: 'none',
            '&::-webkit-scrollbar': { display: 'none' },
            flex: 1,
            justifyContent: 'center',
            px: 0.6,
            py: 0.3,
            borderRadius: '8px',
            bgcolor: hasSelection ? '#eff6ff' : 'transparent',
            border: hasSelection ? '1px solid #bfdbfe' : '1px solid transparent',
            transition: 'all 0.15s ease',
          }}
        >
          {hasSelection ? (
            <Chip
              label="SEL"
              size="small"
              sx={{
                height: 16,
                fontSize: '0.55rem',
                fontWeight: 900,
                bgcolor: '#2563eb',
                color: '#ffffff',
                '& .MuiChip-label': { px: 0.4 },
                flexShrink: 0,
              }}
            />
          ) : (
            <Typography sx={{ fontSize: '0.58rem', fontWeight: 800, color: '#64748b', textTransform: 'uppercase', flexShrink: 0 }}>
              TOTAL:
            </Typography>
          )}

          {displayEntries.map(([div, data]) => {
            const sym = div === 'EUR' ? '€' : '$';
            const badgeBg = div === 'USD' ? '#fef3c7' : div === 'EUR' ? '#e0e7ff' : '#e0f2fe';
            const badgeColor = div === 'USD' ? '#b45309' : div === 'EUR' ? '#4338ca' : '#0369a1';
            return (
              <Box key={div} sx={{ display: 'flex', alignItems: 'center', gap: 0.25, flexShrink: 0 }}>
                <Chip label={div} size="small" sx={{ height: 16, fontSize: '0.55rem', fontWeight: 900, bgcolor: badgeBg, color: badgeColor, '& .MuiChip-label': { px: 0.4 } }} />
                <Typography sx={{ fontSize: '0.70rem', fontWeight: 900, color: '#0f172a', whiteSpace: 'nowrap' }}>
                  {sym}{fmt(data.total)}
                </Typography>
              </Box>
            );
          })}
          {expanded ? <ExpandMoreIcon sx={{ fontSize: 16, color: '#64748b', flexShrink: 0 }} /> : <ExpandLessIcon sx={{ fontSize: 16, color: '#64748b', flexShrink: 0 }} />}
        </Box>

        {/* Botón Acción: Solicitar a Pago o Consulta */}
        <Button
          size="small"
          variant="contained"
          onClick={() => onGenerarExcel(isReadyTab)}
          startIcon={<FileDownloadIcon sx={{ fontSize: 13 }} />}
          sx={{
            height: 28,
            px: 0.9,
            fontSize: '0.66rem',
            fontWeight: 800,
            textTransform: 'none',
            borderRadius: '8px',
            bgcolor: isReadyTab ? '#16a34a' : '#0284c7',
            color: '#ffffff',
            boxShadow: 'none',
            flexShrink: 0,
            whiteSpace: 'nowrap',
            '&:hover': { bgcolor: isReadyTab ? '#15803d' : '#0369a1' }
          }}
        >
          {isReadyTab ? (hasSelection ? `A Pago (${selectedCount})` : 'A Pago') : 'Excel'}
        </Button>
      </Box>

      {/* Detalle Desplegado de Totales */}
      <Collapse in={expanded} timeout="auto" unmountOnExit>
        <Box sx={{ mt: 1, pt: 0.8, borderTop: '1px solid #e2e8f0', display: 'flex', flexDirection: 'column', gap: 0.8 }}>
          {hasSelection && (
            <Box sx={{ bgcolor: '#eff6ff', p: 0.8, borderRadius: '8px', border: '1px solid #bfdbfe' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
                <Typography sx={{ fontSize: '0.62rem', fontWeight: 900, color: '#1d4ed8', textTransform: 'uppercase' }}>
                  Seleccionadas ({selectedCount} órdenes)
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.4 }}>
                {selectedEntries.map(([divisa, vals]) => {
                  const sym = divisa === 'EUR' ? '€' : '$';
                  const colorMain = divisa === 'USD' ? '#b45309' : divisa === 'EUR' ? '#4338ca' : '#0369a1';
                  const bgBadge = divisa === 'USD' ? '#fef3c7' : divisa === 'EUR' ? '#e0e7ff' : '#e0f2fe';
                  return (
                    <Box key={`sel_${divisa}`} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', bgcolor: '#ffffff', p: 0.5, px: 0.8, borderRadius: '6px', border: '1px solid #dbeafe' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
                        <Chip label={divisa} size="small" sx={{ height: 18, fontSize: '0.60rem', fontWeight: 900, color: colorMain, bgcolor: bgBadge }} />
                        <Typography sx={{ fontSize: '0.66rem', fontWeight: 700, color: '#64748b' }}>
                          {vals.count} órdenes
                        </Typography>
                      </Box>
                      <Typography sx={{ fontSize: '0.78rem', fontWeight: 900, color: '#0f172a' }}>
                        {sym}{fmt(vals.total)}
                      </Typography>
                    </Box>
                  );
                })}
              </Box>
            </Box>
          )}

          {/* Total general de las mostradas */}
          <Box sx={{ bgcolor: '#f8fafc', p: 0.8, borderRadius: '8px', border: '1px solid #f1f5f9' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
              <Typography sx={{ fontSize: '0.62rem', fontWeight: 800, color: '#64748b', textTransform: 'uppercase' }}>
                Total General ({preparedRows.length} órdenes)
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.4 }}>
              {totalEntries.map(([divisa, vals]) => {
                const sym = divisa === 'EUR' ? '€' : '$';
                const colorMain = divisa === 'USD' ? '#b45309' : divisa === 'EUR' ? '#4338ca' : '#0369a1';
                const bgBadge = divisa === 'USD' ? '#fef3c7' : divisa === 'EUR' ? '#e0e7ff' : '#e0f2fe';
                return (
                  <Box key={`tot_${divisa}`} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', bgcolor: '#ffffff', p: 0.5, px: 0.8, borderRadius: '6px', border: '1px solid #f1f5f9' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
                      <Chip label={divisa} size="small" sx={{ height: 18, fontSize: '0.60rem', fontWeight: 900, color: colorMain, bgcolor: bgBadge }} />
                      <Typography sx={{ fontSize: '0.66rem', fontWeight: 700, color: '#64748b' }}>
                        {vals.count} órdenes
                      </Typography>
                    </Box>
                    <Typography sx={{ fontSize: '0.78rem', fontWeight: 900, color: '#0f172a' }}>
                      {sym}{fmt(vals.total)}
                    </Typography>
                  </Box>
                );
              })}
            </Box>
          </Box>
        </Box>
      </Collapse>
    </Box>
  );
});

export default function ReporteAutorizadas() {
  const { loggedInUsername, idEmpresa, idPerfil, usuarioId } = useSession();
  const apiUrl = config.apiBaseUrl;

  const [data, setData] = useState([]);
  const [empresaOptions, setEmpresaOptions] = useState([]);
  const [proyectosCatalogo, setProyectosCatalogo] = useState([]);
  const [divisionesCatalogo, setDivisionesCatalogo] = useState([]);
  const [centrosCostoCatalogo, setCentrosCostoCatalogo] = useState([]);
  const [filterCatalogOptions, setFilterCatalogOptions] = useState({
    departamentos: [],
    centrosCosto: [],
    proveedores: [],
    divisas: [],
  });
  const [loading, setLoading] = useState(false);
  const [historialMap, setHistorialMap] = useState({});
  const [autorizarChecked, setAutorizarChecked] = useState({});
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY_ROWS_PER_PAGE);
      if (saved !== null) {
        const val = parseInt(saved, 10);
        if (!isNaN(val)) return val;
      }
    } catch {}
    return 25;
  });
  const [activeTab, setActiveTab] = useState(TAB_LISTAS);
  const [hierarchicalAnchorEl, setHierarchicalAnchorEl] = useState(null);
  const [filterAnchorEl, setFilterAnchorEl] = useState(null);
  const [showFiltersPanel, setShowFiltersPanel] = useState(false);
  const [columnasModalOpen, setColumnasModalOpen] = useState(false);
  const [mobileExcelAnchorEl, setMobileExcelAnchorEl] = useState(null);

  // ── Refs para evitar llamadas repetidas a la API y aislar el popover ──
  const lastLoadedEmpresasRef = useRef('');
  const lastLoadedOpcionesEmpresasRef = useRef('');
  const totalesPopoverRef = useRef(null);
  const breakdownRef = useRef({});

  // ─── Preferencias persistentes de Columnas Visibles y Orden (Local + DB) ───
  const defaultColumnIds = useMemo(() => ALL_COLUMNS_DEF.map(c => c.id), []);

  const [columnOrder, setColumnOrder] = useState(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY_COLUMN_ORDER);
      if (saved) {
        const parsed = JSON.parse(saved);
        if (Array.isArray(parsed) && parsed.length) {
          const merged = [...parsed.filter(id => defaultColumnIds.includes(id))];
          defaultColumnIds.forEach(id => {
            if (!merged.includes(id)) merged.push(id);
          });
          return merged;
        }
      }
    } catch {}
    return defaultColumnIds;
  });

  const [visibleCols, setVisibleCols] = useState(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY_COLUMNS);
      if (saved) {
        return JSON.parse(saved);
      }
    } catch {}
    const initial = {};
    ALL_COLUMNS_DEF.forEach(col => {
      initial[col.id] = col.defaultVisible;
    });
    return initial;
  });

  const readyToSavePrefsRef = useRef(false);

  // 1. Cargar preferencias desde base de datos al montar
  useEffect(() => {
    fetchUserPrefs({ modulo: 'ReporteAutorizadas', vista: 'main' })
      .then(pref => {
        if (pref && typeof pref === 'object') {
          if (pref.visibleCols && typeof pref.visibleCols === 'object') {
            setVisibleCols(prev => ({ ...prev, ...pref.visibleCols }));
            try { localStorage.setItem(STORAGE_KEY_COLUMNS, JSON.stringify({ ...visibleCols, ...pref.visibleCols })); } catch {}
          }
          if (Array.isArray(pref.columnOrder) && pref.columnOrder.length) {
            const merged = [...pref.columnOrder.filter(id => defaultColumnIds.includes(id))];
            defaultColumnIds.forEach(id => {
              if (!merged.includes(id)) merged.push(id);
            });
            setColumnOrder(merged);
            try { localStorage.setItem(STORAGE_KEY_COLUMN_ORDER, JSON.stringify(merged)); } catch {}
          }
          if (pref.rowsPerPage !== undefined) {
            const rpp = parseInt(pref.rowsPerPage, 10);
            if (!isNaN(rpp)) {
              setRowsPerPage(rpp);
              try { localStorage.setItem(STORAGE_KEY_ROWS_PER_PAGE, String(rpp)); } catch {}
            }
          }
        }
      })
      .catch(err => console.error('Error cargando preferencias de ReporteAutorizadas:', err))
      .finally(() => {
        setTimeout(() => {
          readyToSavePrefsRef.current = true;
        }, 400);
      });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const savePreferences = useCallback((newVisibleCols, newColumnOrder, newRowsPerPage) => {
    const finalCols = newVisibleCols || visibleCols;
    const finalOrder = newColumnOrder || columnOrder;
    const finalRpp = newRowsPerPage !== undefined ? newRowsPerPage : rowsPerPage;

    try {
      if (newVisibleCols) localStorage.setItem(STORAGE_KEY_COLUMNS, JSON.stringify(newVisibleCols));
      if (newColumnOrder) localStorage.setItem(STORAGE_KEY_COLUMN_ORDER, JSON.stringify(newColumnOrder));
      if (newRowsPerPage !== undefined) localStorage.setItem(STORAGE_KEY_ROWS_PER_PAGE, String(newRowsPerPage));
    } catch {}

    if (!readyToSavePrefsRef.current) return;
    saveUserPrefs({
      modulo: 'ReporteAutorizadas',
      vista: 'main',
      pref: {
        visibleCols: finalCols,
        columnOrder: finalOrder,
        rowsPerPage: finalRpp,
      }
    }).catch(err => console.error('Error guardando preferencias en DB:', err));
  }, [visibleCols, columnOrder, rowsPerPage]);

  const toggleColumn = useCallback((colId) => {
    setVisibleCols(prev => {
      const next = { ...prev, [colId]: !prev[colId] };
      savePreferences(next, columnOrder, rowsPerPage);
      return next;
    });
  }, [columnOrder, rowsPerPage, savePreferences]);

  const moveColumn = useCallback((index, direction) => {
    setColumnOrder(prev => {
      const targetIndex = index + direction;
      if (targetIndex < 0 || targetIndex >= prev.length) return prev;
      const copy = [...prev];
      const [moved] = copy.splice(index, 1);
      copy.splice(targetIndex, 0, moved);
      savePreferences(visibleCols, copy, rowsPerPage);
      return copy;
    });
  }, [visibleCols, rowsPerPage, savePreferences]);

  const resetColumns = useCallback(() => {
    const initial = {};
    ALL_COLUMNS_DEF.forEach(col => {
      initial[col.id] = col.defaultVisible;
    });
    setVisibleCols(initial);
    setColumnOrder(defaultColumnIds);
    savePreferences(initial, defaultColumnIds, rowsPerPage);
  }, [defaultColumnIds, rowsPerPage, savePreferences]);

  const requestIdRef = useRef(0);
  const defaultYearRange = useMemo(() => getCurrentYearRange(), []);
  const sessionEmpresaIds = useMemo(() => normalizeEmpresaArray(idEmpresa), [idEmpresa]);
  const empresaIdFallback = useMemo(() => {
    return sessionEmpresaIds.length === 1 ? sessionEmpresaIds[0] : '';
  }, [sessionEmpresaIds]);

  const normalizeRowForReporte = useCallback((row) => ({
    ...row,
    EMPRESAID: row.EMPRESAID || row.IDEMPRESA || row.empresaId || row.EMPRESA_ID || empresaIdFallback,
  }), [empresaIdFallback]);

  const baseFilters = useMemo(() => ({
    FECHA_INICIO: defaultYearRange.start,
    FECHA_FIN: defaultYearRange.end,
    EMPRESA: '',
    EMPRESA_IDS: [],
    PROYECTOS: [],
    PROYECTO: '',
    DIVISIONES: [],
    DIVISION: '',
    CENTRO_COSTOS: [],
    CENTRO_COSTO: '',
    DEPARTAMENTOS: [],
    PROVEEDORES: [],
    DIVISAS: [],
    BUSQUEDA_RAPIDA: '',
    ESTATUS: 'Autorizada',
  }), [defaultYearRange]);

  const [filterData, setFilterData] = useState(baseFilters);

  const selectedCatalogEmpresaIds = useMemo(() => {
    const selected = Array.isArray(filterData.EMPRESA_IDS)
      ? filterData.EMPRESA_IDS.map(Number).filter(id => Number.isInteger(id) && id > 0)
      : [];
    const scoped = selected.filter(id => sessionEmpresaIds.includes(id));
    if (scoped.length) return scoped;
    return sessionEmpresaIds;
  }, [filterData.EMPRESA_IDS, sessionEmpresaIds]);

  const selectedEmpresaIds = selectedCatalogEmpresaIds;

  const contentFiltersActive = useMemo(
    () => hasActiveContentFilters(filterData),
    [filterData]
  );

  const baseRows = useMemo(() => (
    data
      .map((row) => {
        const prepared = prepareRow(normalizeRowForReporte(row));
        const key = getReporteKey(prepared);
        return {
          ...prepared,
          reporteHistorial: key ? historialMap[key] : null,
        };
      })
      .filter((row) => {
        // Excluir solicitudes que ya tienen Exhibit asignado (ya cuentan con pago o programación)
        const rawExhibit = String(row.exhibit || '').trim();
        const hasExhibit = rawExhibit && rawExhibit !== '-' && rawExhibit !== '0' && rawExhibit.toLowerCase() !== 'null';
        return !hasExhibit;
      })
      .sort((a, b) => {
        const hasCompA = a.infoCompromiso && a.infoCompromiso.nivel !== 'sin_fecha' && a.infoCompromiso.dias !== null;
        const hasCompB = b.infoCompromiso && b.infoCompromiso.nivel !== 'sin_fecha' && b.infoCompromiso.dias !== null;

        // Si ambos tienen fecha compromiso: ordenar del que más pronto vence (menor días) al que más tiempo tiene
        if (hasCompA && hasCompB) {
          if (a.infoCompromiso.dias !== b.infoCompromiso.dias) {
            return a.infoCompromiso.dias - b.infoCompromiso.dias;
          }
          return (b.fechaSort - a.fechaSort) || (Number(b.ID) - Number(a.ID));
        }

        // El que tiene fecha compromiso va primero
        if (hasCompA && !hasCompB) return -1;
        if (!hasCompA && hasCompB) return 1;

        // Si ninguno tiene fecha compromiso: ordenar por fecha más reciente
        return (b.fechaSort - a.fechaSort) || (Number(b.ID) - Number(a.ID));
      })
  ), [data, historialMap, normalizeRowForReporte]);

  const dateFilteredRows = useMemo(() => {
    const startTime = filterData.FECHA_INICIO
      ? new Date(`${filterData.FECHA_INICIO}T00:00:00`).getTime()
      : null;
    const endTime = filterData.FECHA_FIN
      ? new Date(`${filterData.FECHA_FIN}T23:59:59`).getTime()
      : null;

    return baseRows.filter((row) => {
      if (startTime && (!row.fechaSort || row.fechaSort < startTime)) return false;
      if (endTime && (!row.fechaSort || row.fechaSort > endTime)) return false;
      return true;
    });
  }, [baseRows, filterData.FECHA_FIN, filterData.FECHA_INICIO]);

  // Cargar Catálogos de Proyectos, Divisiones y Centros de Costo (acotados a los permisos del usuario)
  useEffect(() => {
    if (!apiUrl || !selectedCatalogEmpresaIds.length) return;

    const empresasKey = selectedCatalogEmpresaIds.slice().sort().join(',');
    if (lastLoadedEmpresasRef.current === empresasKey) return;
    lastLoadedEmpresasRef.current = empresasKey;

    axios.post(`${apiUrl}/proyectosTabla`, { idEmpresa: selectedCatalogEmpresaIds, usuarioId, idPerfil })
      .then(res => {
        const raw = res.data?.contenido || (Array.isArray(res.data) ? res.data : []);
        setProyectosCatalogo(raw.filter(p => Boolean(p.ACTIVO)));
      })
      .catch(() => setProyectosCatalogo([]));

    axios.post(`${apiUrl}/divisionesTabla`, { idEmpresa: selectedCatalogEmpresaIds, usuarioId, idPerfil })
      .then(res => {
        const raw = res.data?.contenido || (Array.isArray(res.data) ? res.data : []);
        setDivisionesCatalogo(raw.filter(d => Boolean(d.ACTIVO)));
      })
      .catch(() => setDivisionesCatalogo([]));

    axios.post(`${apiUrl}/centroCostosTabla`, { idEmpresa: selectedCatalogEmpresaIds, usuarioId, idPerfil })
      .then(res => {
        const raw = res.data?.contenido || (Array.isArray(res.data) ? res.data : []);
        setCentrosCostoCatalogo(raw.filter(c => Boolean(c.ACTIVO)));
      })
      .catch(() => setCentrosCostoCatalogo([]));
  }, [apiUrl, selectedCatalogEmpresaIds, usuarioId, idPerfil]);

  // ─── Opciones Dinámicas en Cascada Jerárquica ───
  const proyectoOptions = useMemo(() => {
    const allowedProyIds = new Set(
      centrosCostoCatalogo.map(c => Number(c.ID_PROYECTO)).filter(id => id > 0)
    );

    const map = new Map();
    proyectosCatalogo.forEach(p => {
      const pid = Number(p.ID_PROYECTO);
      if (centrosCostoCatalogo.length === 0 || allowedProyIds.has(pid)) {
        if (p.ID_PROYECTO && p.NOMBRE_PROYECTO) {
          map.set(pid, p.NOMBRE_PROYECTO);
        }
      }
    });
    const list = Array.from(map.entries()).map(([id, nombre]) => ({ id, nombre }));
    list.sort((a, b) => a.nombre.localeCompare(b.nombre, 'es'));
    return list;
  }, [proyectosCatalogo, centrosCostoCatalogo]);

  const divisionOptions = useMemo(() => {
    const selectedProyIds = (filterData.PROYECTOS || []).map(Number);
    const allowedDivIds = new Set(
      centrosCostoCatalogo.map(c => Number(c.ID_DIVISION)).filter(id => id > 0)
    );

    const map = new Map();
    divisionesCatalogo.forEach(d => {
      const pId = Number(d.ID_PROYECTO);
      const dId = Number(d.ID_DIVISION);
      
      const matchProy = !selectedProyIds.length || selectedProyIds.includes(pId);
      const matchAllowed = centrosCostoCatalogo.length === 0 || allowedDivIds.has(dId);

      if (matchProy && matchAllowed) {
        if (d.ID_DIVISION && d.NOMBRE_DIVISION) {
          map.set(dId, d.NOMBRE_DIVISION);
        }
      }
    });
    const list = Array.from(map.entries()).map(([id, nombre]) => ({ id, nombre }));
    list.sort((a, b) => a.nombre.localeCompare(b.nombre, 'es'));
    return list;
  }, [divisionesCatalogo, filterData.PROYECTOS, centrosCostoCatalogo]);

  const centroCostoOptions = useMemo(() => {
    const selectedProyIds = (filterData.PROYECTOS || []).map(Number);
    const selectedDivIds = (filterData.DIVISIONES || []).map(Number);

    let filtered = centrosCostoCatalogo;
    if (selectedProyIds.length > 0) {
      filtered = filtered.filter(c => selectedProyIds.includes(Number(c.ID_PROYECTO)));
    }
    if (selectedDivIds.length > 0) {
      filtered = filtered.filter(c => selectedDivIds.includes(Number(c.ID_DIVISION)));
    }

    const ccList = filtered.map(c => {
      const num = String(c.NUMERO_CC || c.NUMERO || '').trim();
      const nom = String(c.CENTRO_COSTO || c.NOMBRE || '').trim();
      if (num && nom && num.toUpperCase() !== nom.toUpperCase()) return `${num} - ${nom}`;
      return num || nom;
    }).filter(Boolean);

    if (ccList.length > 0) {
      return Array.from(new Set(ccList)).sort((a, b) => a.localeCompare(b, 'es'));
    }

    return getUniqueOptions([
      ...filterCatalogOptions.centrosCosto,
      ...baseRows.map(row => formatCentroCostoOption(row.centroCostoNumero, row.centroCostoNombre)),
    ]);
  }, [centrosCostoCatalogo, filterData.PROYECTOS, filterData.DIVISIONES, filterCatalogOptions.centrosCosto, baseRows]);

  // Resumen visual estilo Breadcrumbs: Empresa > Proyecto > Grupo > CC
  const hierarchicalSummary = useMemo(() => {
    const parts = [];
    const empCount = (filterData.EMPRESA_IDS || []).length;
    if (empCount === 1) {
      const emp = empresaOptions.find(e => e.id === filterData.EMPRESA_IDS[0]);
      if (emp) parts.push(emp.nombre);
    } else if (empCount > 1) {
      parts.push(`${empCount} Emp`);
    }

    const proyCount = (filterData.PROYECTOS || []).length;
    if (proyCount === 1) {
      const proy = proyectoOptions.find(p => p.id === filterData.PROYECTOS[0]);
      if (proy) parts.push(proy.nombre);
    } else if (proyCount > 1) {
      parts.push(`${proyCount} Proy`);
    }

    const divCount = (filterData.DIVISIONES || []).length;
    if (divCount === 1) {
      const div = divisionOptions.find(d => d.id === filterData.DIVISIONES[0]);
      if (div) parts.push(div.nombre);
    } else if (divCount > 1) {
      parts.push(`${divCount} Grupos`);
    }

    const ccCount = (filterData.CENTRO_COSTOS || []).length;
    if (ccCount === 1) {
      parts.push(filterData.CENTRO_COSTOS[0].split(' - ')[0] || filterData.CENTRO_COSTOS[0]);
    } else if (ccCount > 1) {
      parts.push(`${ccCount} CCs`);
    }

    if (!parts.length) return 'Empresa / Proyecto / Grupo / CC';
    if (empCount > 0 && !proyCount && !divCount && !ccCount) {
      return `${parts[0]} › Proyecto / Grupo / CC`;
    }
    return parts.join(' › ');
  }, [filterData.EMPRESA_IDS, filterData.PROYECTOS, filterData.DIVISIONES, filterData.CENTRO_COSTOS, empresaOptions, proyectoOptions, divisionOptions]);

  const hasHierarchicalFilter = useMemo(() => {
    return (filterData.EMPRESA_IDS || []).length > 0 ||
      (filterData.PROYECTOS || []).length > 0 ||
      (filterData.DIVISIONES || []).length > 0 ||
      (filterData.CENTRO_COSTOS || []).length > 0;
  }, [filterData.EMPRESA_IDS, filterData.PROYECTOS, filterData.DIVISIONES, filterData.CENTRO_COSTOS]);

  const extraFiltersCount = useMemo(() => {
    return (filterData.DEPARTAMENTOS || []).length +
      (filterData.PROVEEDORES || []).length +
      (filterData.DIVISAS || []).length;
  }, [filterData.DEPARTAMENTOS, filterData.PROVEEDORES, filterData.DIVISAS]);

  const hasAnyActiveFilter = useMemo(() => {
    return Boolean(
      filterData.FECHA_INICIO ||
      filterData.FECHA_FIN ||
      filterData.BUSQUEDA_RAPIDA ||
      hasHierarchicalFilter ||
      extraFiltersCount > 0
    );
  }, [filterData.FECHA_INICIO, filterData.FECHA_FIN, filterData.BUSQUEDA_RAPIDA, hasHierarchicalFilter, extraFiltersCount]);

  const departamentoOptions = useMemo(
    () => getUniqueOptions([
      ...filterCatalogOptions.departamentos,
      ...baseRows.map(row => row.departamento),
    ]),
    [baseRows, filterCatalogOptions.departamentos]
  );

  const proveedorOptions = useMemo(
    () => getUniqueOptions([
      ...filterCatalogOptions.proveedores,
      ...baseRows.map(row => row.proveedor),
    ]),
    [baseRows, filterCatalogOptions.proveedores]
  );

  const divisaOptions = useMemo(
    () => getUniqueOptions([
      ...filterCatalogOptions.divisas,
      ...baseRows.map(row => row.moneda),
    ]),
    [baseRows, filterCatalogOptions.divisas]
  );

  const filteredRows = useMemo(() => {
    const quickSearch = normalizeText(filterData.BUSQUEDA_RAPIDA);
    const sourceRows = contentFiltersActive ? baseRows : dateFilteredRows;

    // Resolver centros de costo permitidos para proyectos/divisiones
    const selectedProyIds = (filterData.PROYECTOS || []).map(Number);
    const selectedDivIds = (filterData.DIVISIONES || []).map(Number);

    // Conjuntos de centros de costo válidos según el catálogo
    let allowedCcKeysFromHierarchy = null;
    if (selectedDivIds.length > 0 || selectedProyIds.length > 0) {
      const ccs = centrosCostoCatalogo.filter(c => {
        const matchDiv = !selectedDivIds.length || selectedDivIds.includes(Number(c.ID_DIVISION));
        const matchProy = !selectedProyIds.length || selectedProyIds.includes(Number(c.ID_PROYECTO));
        return matchDiv && matchProy;
      });
      allowedCcKeysFromHierarchy = new Set(
        ccs.flatMap(c => [
          normalizeText(c.NUMERO_CC || c.NUMERO),
          normalizeText(c.CENTRO_COSTO || c.NOMBRE)
        ]).filter(Boolean)
      );
    }

    return sourceRows
      .filter((row) => {
        if (selectedEmpresaIds.length && row.empresaId && !selectedEmpresaIds.includes(Number(row.empresaId))) return false;

        // Si se seleccionó Proyecto o División, la solicitud DEBE pertenecer a dicho proyecto/división
        if (selectedProyIds.length > 0) {
          const rowProyId = Number(row.ID_PROYECTO || 0);
          const matchRowDirect = rowProyId > 0 && selectedProyIds.includes(rowProyId);
          const matchCatalogCc = allowedCcKeysFromHierarchy && (
            allowedCcKeysFromHierarchy.has(normalizeText(row.centroCostoNumero)) ||
            allowedCcKeysFromHierarchy.has(normalizeText(row.centroCostoNombre))
          );
          if (!matchRowDirect && !matchCatalogCc) return false;
        }

        if (selectedDivIds.length > 0) {
          const rowDivId = Number(row.ID_DIVISION || 0);
          const matchRowDirect = rowDivId > 0 && selectedDivIds.includes(rowDivId);
          const matchCatalogCc = allowedCcKeysFromHierarchy && (
            allowedCcKeysFromHierarchy.has(normalizeText(row.centroCostoNumero)) ||
            allowedCcKeysFromHierarchy.has(normalizeText(row.centroCostoNombre))
          );
          if (!matchRowDirect && !matchCatalogCc) return false;
        }

        const departamentos = Array.isArray(filterData.DEPARTAMENTOS) ? filterData.DEPARTAMENTOS : [];
        if (
          departamentos.length &&
          !departamentos.some(departamento => normalizeText(row.departamento).includes(normalizeText(departamento)))
        ) return false;

        const centrosCosto = Array.isArray(filterData.CENTRO_COSTOS) ? filterData.CENTRO_COSTOS : [];
        if (
          centrosCosto.length &&
          !getCentroCostoFilterValues(centrosCosto)
            .some(centroCosto => normalizeText(row.centroCostoNombre || row.centroCosto).includes(normalizeText(centroCosto)))
        ) return false;

        const proveedores = Array.isArray(filterData.PROVEEDORES) ? filterData.PROVEEDORES : [];
        if (
          proveedores.length &&
          !proveedores.some(proveedor => normalizeText(row.proveedor).includes(normalizeText(proveedor)))
        ) return false;

        const divisas = Array.isArray(filterData.DIVISAS) ? filterData.DIVISAS : [];
        if (
          divisas.length &&
          !divisas.some(divisa => normalizeText(row.moneda) === normalizeText(divisa))
        ) return false;

        if (quickSearch) {
          const searchable = normalizeText([
            row.orden,
            row.empresa,
            row.proveedor,
            row.concepto,
            row.departamento,
            row.centroCosto,
            row.NUMERO_ECONOMICO,
            row.moneda,
          ].join(' '));
          if (!searchable.includes(quickSearch)) return false;
        }

        return true;
      });
  }, [baseRows, contentFiltersActive, dateFilteredRows, filterData, selectedEmpresaIds]);

  const readyRows = useMemo(
    () => filteredRows.filter(row => row.estatusId === 3),
    [filteredRows]
  );

  const pendingRows = useMemo(
    () => filteredRows.filter(row => row.estatusId === 1 || row.estatusId === 2 || row.estatusId === 11),
    [filteredRows]
  );

  const allRows = filteredRows;

  const isReadyTab = activeTab === TAB_LISTAS;

  const preparedRows = useMemo(() => {
    return isReadyTab ? readyRows : pendingRows;
  }, [isReadyTab, pendingRows, readyRows]);

  const pageRows = useMemo(() => {
    if (rowsPerPage === -1) return preparedRows;
    const start = page * rowsPerPage;
    return preparedRows.slice(start, start + rowsPerPage);
  }, [page, preparedRows, rowsPerPage]);

  const RENDER_CHUNK = 50;
  const [renderLimit, setRenderLimit] = useState(RENDER_CHUNK);

  useEffect(() => {
    setRenderLimit(RENDER_CHUNK);
  }, [page, rowsPerPage, activeTab, filterData]);

  const displayedRows = useMemo(() => {
    return pageRows.length > renderLimit ? pageRows.slice(0, renderLimit) : pageRows;
  }, [pageRows, renderLimit]);

  const hasMoreToRender = renderLimit < pageRows.length;

  const renderMore = useCallback(() => {
    setRenderLimit(prev => Math.min(prev + RENDER_CHUNK, pageRows.length));
  }, [pageRows.length]);

  const scrollRafRef = useRef(null);
  const handleContainerScroll = useCallback((e) => {
    const el = e.currentTarget;
    if (!hasMoreToRender || scrollRafRef.current) return;
    scrollRafRef.current = requestAnimationFrame(() => {
      scrollRafRef.current = null;
      if (el.scrollTop + el.clientHeight >= el.scrollHeight - 500) {
        renderMore();
      }
    });
  }, [hasMoreToRender, renderMore]);

  const paginatedRows = displayedRows;

  const selectedRows = useMemo(
    () => readyRows.filter(row => autorizarChecked[row.ID]),
    [autorizarChecked, readyRows]
  );

  const selectedCount = selectedRows.length;

  const visibleTotals = useMemo(() => {
    const totals = {};
    preparedRows.forEach((row) => {
      const moneda = (row.moneda || 'MXN').toString().toUpperCase();
      totals[moneda] = (totals[moneda] || 0) + parseMoney(row.pago);
    });
    return Object.entries(totals).sort(([currencyA], [currencyB]) => currencyA.localeCompare(currencyB));
  }, [preparedRows]);

  const selectedTotals = useMemo(() => {
    const totals = {};
    selectedRows.forEach((row) => {
      const moneda = (row.moneda || 'MXN').toString().toUpperCase();
      totals[moneda] = (totals[moneda] || 0) + parseMoney(row.pago);
    });
    return Object.entries(totals).sort(([currencyA], [currencyB]) => currencyA.localeCompare(currencyB));
  }, [selectedRows]);

  const allReadySelected = useMemo(() => {
    return readyRows.length > 0 && readyRows.every(r => !!autorizarChecked[r.ID]);
  }, [readyRows, autorizarChecked]);

  const handleToggleSelectAll = useCallback(() => {
    if (allReadySelected) {
      setAutorizarChecked({});
    } else {
      const next = {};
      readyRows.forEach(r => { next[r.ID] = true; });
      setAutorizarChecked(next);
    }
  }, [allReadySelected, readyRows]);

  const cargarHistorial = useCallback(async (rows, requestId) => {
    const items = rows.map(row => buildHistorialItem(prepareRow(normalizeRowForReporte(row))));
    if (!items.length) {
      if (requestId !== requestIdRef.current) return;
      setHistorialMap({});
      return;
    }

    try {
      const response = await axios.post(`${apiUrl}/reporteAutorizadas/historial`, { items });
      if (requestId !== requestIdRef.current) return;
      setHistorialMap(response.data?.historial || {});
    } catch (err) {
      if (requestId !== requestIdRef.current) return;
      console.error('Error consultando historial de reporte autorizadas:', err);
      setHistorialMap({});
    }
  }, [apiUrl, normalizeRowForReporte]);

  useEffect(() => {
    const cargarEmpresas = async () => {
      if (!apiUrl || !usuarioId) return;

      try {
        const response = await axios.get(`${apiUrl}/api/empresas/${usuarioId}`);
        const empresas = response.data?.empresas || [];
        const mapped = empresas
          .map(empresa => ({
            id: Number(empresa.EMPRESAID),
            nombre: empresa.NOMBRE_EMPRESA,
          }))
          .filter(empresa => sessionEmpresaIds.includes(empresa.id));
        setEmpresaOptions(mapped);
        if (mapped.length === 1) {
          setFilterData(prev => (
            (prev.EMPRESA_IDS || []).length ? prev : { ...prev, EMPRESA_IDS: [mapped[0].id] }
          ));
        }
      } catch (err) {
        console.error('Error cargando empresas para reporte autorizadas:', err);
        setEmpresaOptions([]);
      }
    };

    cargarEmpresas();
  }, [apiUrl, usuarioId, sessionEmpresaIds]);

  useEffect(() => {
    const cargarOpcionesFiltros = async () => {
      if (!apiUrl || !selectedEmpresaIds.length) {
        setFilterCatalogOptions({
          departamentos: [],
          centrosCosto: [],
          proveedores: [],
          divisas: [],
        });
        return;
      }

      const opcionesKey = selectedEmpresaIds.slice().sort().join(',');
      if (lastLoadedOpcionesEmpresasRef.current === opcionesKey) return;
      lastLoadedOpcionesEmpresasRef.current = opcionesKey;

      try {
        const response = await axios.post(`${apiUrl}/solicitudesProgramadas/opcionesFiltros`, {
          idEmpresa: selectedEmpresaIds,
          filterData: {
            FECHA_INICIO: null,
            FECHA_FIN: null,
            ESTATUS: '',
          },
        });

        setFilterCatalogOptions({
          departamentos: response.data?.departamentos || [],
          centrosCosto: response.data?.centrosCosto || [],
          proveedores: response.data?.proveedores || [],
          divisas: response.data?.divisas || [],
        });
      } catch (err) {
        console.error('Error cargando opciones para reporte autorizadas:', err);
        setFilterCatalogOptions({
          departamentos: [],
          centrosCosto: [],
          proveedores: [],
          divisas: [],
        });
      }
    };

    cargarOpcionesFiltros();
  }, [apiUrl, selectedEmpresaIds]);

  const fetchData = useCallback(async () => {
    if (!apiUrl || !loggedInUsername || !selectedEmpresaIds.length || !idPerfil) return;
    const requestId = requestIdRef.current + 1;
    requestIdRef.current = requestId;
    setLoading(true);
    try {
      const response = await axios.post(`${apiUrl}/reporteAutorizadasAdmin`, {
        idEmpresa: selectedEmpresaIds,
        usuarioId,
        idPerfil,
        filterData: {
          FECHA_INICIO: filterData.FECHA_INICIO,
          FECHA_FIN: filterData.FECHA_FIN,
        },
      });

      if (requestId !== requestIdRef.current) return;

      const contenido = response.data?.contenido || [];

      const selectedDivisas = filterData.DIVISAS || [];
      const divisaSet = new Set(selectedDivisas.map(divisa => String(divisa).trim().toUpperCase()));
      const uniqueRows = new Map();

      contenido.forEach((row) => {
        const normalized = normalizeRowForReporte(row);
        const estatusId = getReporteStatusId(normalized);
        

        if (!REPORTE_STATUS_IDS.has(estatusId)) return;
        if (selectedDivisas.length && !divisaSet.has(String(normalized.DIVISA || '').trim().toUpperCase())) return;

        const key = getReporteKey(normalized) || `${normalized.EMPRESAID || ''}|${normalized.FOLIO || normalized.FOLIO_OC || ''}|${normalized.ID || ''}`;
        if (key && !uniqueRows.has(key)) uniqueRows.set(key, normalized);
      });

      const filtradas = [...uniqueRows.values()];
      setData(filtradas);
      setHistorialMap({});
      cargarHistorial(filtradas, requestId);
    } catch (err) {
      console.error(err);
      handleOpenSweetAlert('No se pudieron cargar los datos', 'error');
    } finally {
      if (requestId === requestIdRef.current) setLoading(false);
    }
  }, [
    apiUrl,
    cargarHistorial,
    filterData.DIVISAS,
    filterData.FECHA_FIN,
    filterData.FECHA_INICIO,
    idPerfil,
    loggedInUsername,
    normalizeRowForReporte,
    selectedEmpresaIds,
    usuarioId,
  ]);

  useEffect(() => {
    const timer = setTimeout(() => {
      fetchData();
    }, 80);
    return () => clearTimeout(timer);
  }, [fetchData]);

  useEffect(() => {
    setPage(0);
  }, [activeTab, filterData]);

  const updateFilter = useCallback((field, value) => {
    setFilterData(prev => ({ ...prev, [field]: value }));
  }, []);

  const toggleAutorizar = useCallback((id) => {
    setAutorizarChecked(prev => ({ ...prev, [id]: !prev[id] }));
  }, []);

  const marcarTodas = () => {
    const next = {};
    readyRows.forEach(row => { next[row.ID] = true; });
    setAutorizarChecked(next);
  };

  const marcarPagina = () => {
    if (!isReadyTab) return;
    setAutorizarChecked(prev => {
      const next = { ...prev };
      pageRows.forEach(row => { next[row.ID] = true; });
      return next;
    });
  };

  const limpiarSeleccion = () => {
    setAutorizarChecked({});
  };

  const generarExcel = async (esParaPago = false) => {
    const filasAExportar = selectedRows.length > 0 ? selectedRows : preparedRows;

    if (!filasAExportar.length) {
      handleOpenSweetAlert('No hay datos en el rango o filtros actuales para exportar.', 'warning');
      return;
    }

    const esSeleccionManual = selectedRows.length > 0;

    const alertBanner = esParaPago
      ? `
        <div style="background-color: #f0fdf4; border: 1px solid #86efac; border-radius: 6px; padding: clamp(5px, 0.6vw, 8px) clamp(8px, 0.8vw, 10px); margin-bottom: clamp(6px, 0.8vw, 10px); text-align: left;">
          <div style="font-weight: 800; color: #166534; font-size: clamp(0.68rem, 0.64rem + 0.1vw, 0.76rem); display: flex; align-items: center; gap: 4px;">
            <span>📤</span> MODO: SOLICITAR A PAGO
          </div>
          <div style="font-size: clamp(0.60rem, 0.56rem + 0.08vw, 0.68rem); color: #15803d; margin-top: 2px; line-height: 1.25;">
            Esta descarga <strong>SÍ generará un registro en el historial</strong> (+1 vez solicitada) para dar seguimiento a la orden.
          </div>
        </div>
      `
      : `
        <div style="background-color: #eff6ff; border: 1px solid #93c5fd; border-radius: 6px; padding: clamp(5px, 0.6vw, 8px) clamp(8px, 0.8vw, 10px); margin-bottom: clamp(6px, 0.8vw, 10px); text-align: left;">
          <div style="font-weight: 800; color: #1e40af; font-size: clamp(0.68rem, 0.64rem + 0.1vw, 0.76rem); display: flex; align-items: center; gap: 4px;">
            <span>ℹ️</span> MODO: SOLO CONSULTA / REPORTE
          </div>
          <div style="font-size: clamp(0.60rem, 0.56rem + 0.08vw, 0.68rem); color: #1d4ed8; margin-top: 2px; line-height: 1.25;">
            Descarga exclusivamente informativa. <strong>NO genera registro</strong> ni modifica contadores.
          </div>
        </div>
      `;

    const modalHtml = `
      <div style="font-family: inherit; font-size: clamp(0.68rem, 0.64rem + 0.1vw, 0.76rem); text-align: left; color: #334155; padding: 0;">
        ${alertBanner}

        <p style="margin: 0 0 clamp(6px, 0.8vw, 10px) 0; text-align: center; line-height: 1.3; font-weight: 700; color: #0f172a; font-size: clamp(0.70rem, 0.66rem + 0.12vw, 0.80rem);">
          Estructura para <strong>${filasAExportar.length}</strong> órdenes ${esSeleccionManual ? 'seleccionadas' : 'mostradas'}:
        </p>

        <div style="display: flex; flex-direction: column; gap: clamp(4px, 0.5vw, 6px);">
          <label style="display: flex; align-items: flex-start; gap: 8px; padding: clamp(6px, 0.6vw, 8px) clamp(8px, 0.8vw, 10px); border: 1px solid #cbd5e1; border-radius: 7px; cursor: pointer; background: #f8fafc; transition: all 0.15s;">
            <input type="radio" name="swal_excel_mode_rep" value="estandar" checked style="margin-top: 2px; accent-color: ${esParaPago ? '#16a34a' : '#0284c7'}; cursor: pointer;" />
            <div>
              <div style="font-weight: 700; color: #0f172a; font-size: clamp(0.68rem, 0.64rem + 0.1vw, 0.75rem);">📋 Formato Estándar</div>
              <div style="font-size: clamp(0.58rem, 0.54rem + 0.08vw, 0.66rem); color: #64748b; line-height: 1.2;">Todas las órdenes juntas + Hoja con Resumen.</div>
            </div>
          </label>
          <label style="display: flex; align-items: flex-start; gap: 8px; padding: clamp(6px, 0.6vw, 8px) clamp(8px, 0.8vw, 10px); border: 1px solid #cbd5e1; border-radius: 7px; cursor: pointer; background: #f8fafc; transition: all 0.15s;">
            <input type="radio" name="swal_excel_mode_rep" value="por_cc" style="margin-top: 2px; accent-color: ${esParaPago ? '#16a34a' : '#0284c7'}; cursor: pointer;" />
            <div>
              <div style="font-weight: 700; color: #0f172a; font-size: clamp(0.68rem, 0.64rem + 0.1vw, 0.75rem);">📑 Por Centro de Costo (Pestañas)</div>
              <div style="font-size: clamp(0.58rem, 0.54rem + 0.08vw, 0.66rem); color: #64748b; line-height: 1.2;">Una pestaña por cada Centro de Costo + Resumen.</div>
            </div>
          </label>
        </div>
      </div>
    `;

    const result = await handleOpenSweetAlert(modalHtml, 'info', {
      useHtml: true,
      showCancelButton: true,
      confirmButtonText: esParaPago ? 'Solicitar' : 'Descargar',
      cancelButtonText: 'Cancelar',
      width: 'clamp(270px, 85vw, 360px)',
      padding: 'clamp(0.6rem, 1vw, 0.9rem)',
      preConfirm: () => {
        const selectedMode = document.querySelector('input[name="swal_excel_mode_rep"]:checked');
        return {
          modoPorCC: (selectedMode ? selectedMode.value : 'estandar') === 'por_cc',
          guardarEnHistorial: esParaPago,
        };
      }
    });

    if (!result.isConfirmed || !result.value) return;
    const { modoPorCC, guardarEnHistorial } = result.value;
    const seleccionadas = filasAExportar;

    const workbook = new ExcelJS.Workbook();
    workbook.creator = 'Sistema de Pagos ESEASA';
    workbook.created = new Date();

    const sanitizeSheetName = (name, fallback) => {
      let clean = String(name || fallback || 'Hoja')
        .replace(/[*?:/\\\[\]]/g, '_')
        .trim();
      if (clean.length > 31) clean = clean.substring(0, 31);
      return clean || fallback || 'Hoja';
    };

    const getGrupoCentroCosto = (row) => {
      // 0. Si el centro de costo tiene una División o Grupo explícito asignado en el catálogo, usarlo como pestaña
      const divisionCatalogo = String(row.DIVISION || row.GRUPO_CC || row.GRUPO || '').trim();
      if (divisionCatalogo) return divisionCatalogo;

      const rawCc = String(row.centroCostoNombre || row.centroCosto || row.CENTROCOSTO || row.centroCostoNumero || '').trim();
      if (!rawCc) return 'SIN CENTRO COSTO';

      const upper = rawCc.toUpperCase();

      // 1. DUCTOS no se agrupa en una sola familia, cada proyecto de DUCTOS mantiene su hoja individual
      if (upper.includes('DUCTO')) {
        return rawCc;
      }

      // 2. Familias Principales Conocidas (para agrupar todas sus variantes en una sola pestaña)
      if (upper.includes('INDIRECTO')) {
        return 'INDIRECTOS';
      }
      if (upper.includes('INFRAESTRUCTURA')) {
        return 'INFRAESTRUCTURA';
      }
      if (upper.includes('ESTRUCTURA LIGERA MARINA') || upper.includes('ESTRUCTURA LIGERA')) {
        return 'ESTRUCTURA LIGERA MARINA';
      }
      if (upper.includes('ESTRUCTURA') || upper.includes('ESTRUCTURAS')) {
        return 'ESTRUCTURAS';
      }
      if (upper.includes('MANTENIMIENTO')) {
        return 'MANTENIMIENTO';
      }
      if (upper.includes('FABRICACION') || upper.includes('FABRICACIÓN')) {
        return 'FABRICACIÓN';
      }
      if (upper.includes('ADMINISTRAC') || upper.includes('ADMINISTRAT')) {
        return 'ADMINISTRACIÓN';
      }
      if (upper.includes('OPERACION') || upper.includes('OPERACIÓN')) {
        return 'OPERACIÓN';
      }
      if (upper.includes('LOGISTICA') || upper.includes('LOGÍSTICA')) {
        return 'LOGÍSTICA';
      }
      if (upper.includes('TRANSPORTE')) {
        return 'TRANSPORTES';
      }

      // 3. Limpieza general para otras variantes (quitar terminaciones de Anexos, Fases, Genéricas, etc.)
      let base = rawCc
        .replace(/\s*\([^)]*\)\s*$/g, '')
        .replace(/\s*[-–—]\s*(ANEXO|FASE|ETAPA|SUB|REV|PARTE|GENERICO|GENÉRICO|GENERICA|GENÉRICA|\d+).*$/i, '')
        .trim();

      return base || rawCc;
    };

    const renderOrdenesSheet = (sheet, rows) => {
      // 1. Catálogo base de todas las posibles columnas con su extractor de valor y propiedades
      const ALL_EXPORT_COLUMNS = [
        { header: 'ORD', key: 'orden', width: 13, align: 'left', getValue: (r) => r.orden, required: true },
        { header: 'EMPRESA', key: 'empresa', width: 22, align: 'left', getValue: (r) => r.empresa, required: true },
        { header: 'PAGO SOLICITADO', key: 'pago', width: 20, align: 'right', isMoney: true, getValue: (r) => r.pago, required: true },
        { header: 'MONEDA', key: 'moneda', width: 11, align: 'left', getValue: (r) => r.moneda, required: true },
        { header: 'CONDICIÓN DE PAGO', key: 'concepto', width: 48, align: 'left', wrap: true, getValue: (r) => r.concepto },
        { 
          header: 'DESCRIPCIÓN', 
          key: 'descripcion', 
          width: 48, 
          align: 'left', 
          wrap: true, 
          getValue: (r) => {
            if (Array.isArray(r.partidas) && r.partidas.length) {
              return r.partidas.map(p => p.DESCRIPCION_PART || p.DESCRIPCION || '').filter(Boolean).join('\n');
            }
            return r.descripcion || r.DESCRIPCION || r.DESCRIPCION_OC || r.DESCRIPCION_PART || r.CONCEPTOPAGO || r.CONCEPTO || r.OBSERVACIONES || '';
          }
        },
        { header: 'ECONOMICO', key: 'economico', width: 22, align: 'left', getValue: (r) => r.NUMERO_ECONOMICO || r.ECONOMICO || '' },
        { header: 'CENTRO DE COSTOS', key: 'centro_costo', width: 34, align: 'left', wrap: true, getValue: (r) => r.centroCostoNumero || r.centroCosto || '' },
        { header: 'PROVEEDOR', key: 'proveedor', width: 40, align: 'left', wrap: true, getValue: (r) => r.proveedor, required: true },
        { header: 'EXHIBIT', key: 'exhibit', width: 18, align: 'center', getValue: (r) => r.exhibit || r.EXHIBIT || r.EXHIBICION || '' },
        { header: 'COMPROBANTE', key: 'comprobante', width: 22, align: 'center', getValue: (r) => r.comprobante || r.COMPROBANTE || r.REFERENCIA || '' },
        { 
          header: 'F. COMPROMISO', 
          key: 'fecha_compromiso', 
          width: 20, 
          align: 'left', 
          getValue: (r) => r.infoCompromiso?.fechaStr ? `${r.infoCompromiso.fechaStr}${r.infoCompromiso.texto ? ' (' + r.infoCompromiso.texto + ')' : ''}` : '' 
        },
        { header: 'AUTORIZAR', key: 'autorizar', width: 15, align: 'center', isAuth: true, getValue: (r) => autorizarChecked[r.ID] ? CHECK_MARK : '', required: true },
      ];

      // 2. Detección Inteligente: Filtrar solo las columnas que tengan al menos un valor no vacío en el conjunto de filas
      const activeColumns = ALL_EXPORT_COLUMNS.filter(col => {
        if (col.required) return true;
        return rows.some(r => {
          const val = col.getValue(r);
          if (val === null || val === undefined) return false;
          const s = String(val).trim();
          return s !== '' && s !== '-' && s !== '0' && s !== 'N/A';
        });
      });

      sheet.columns = activeColumns.map(col => ({
        header: col.header,
        key: col.key,
        width: col.width
      }));

      sheet.views = [{ state: 'frozen', ySplit: 1 }];
      
      // Auto-filtro adaptativo según cantidad de columnas activas (A1 hasta la última columna)
      const lastColIndex = activeColumns.length;
      const getColLetter = (idx) => {
        let letter = '';
        while (idx > 0) {
          const mod = (idx - 1) % 26;
          letter = String.fromCharCode(65 + mod) + letter;
          idx = Math.floor((idx - mod) / 26);
        }
        return letter || 'A';
      };
      sheet.autoFilter = `A1:${getColLetter(lastColIndex)}1`;

      sheet.getRow(1).height = 24;
      sheet.getRow(1).eachCell((cell) => {
        cell.font = { bold: true, color: { argb: 'FFFFFF' }, size: 10 };
        cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: '2F75B5' } };
        cell.alignment = { horizontal: 'center', vertical: 'middle' };
        applyThinBorder(cell, 'FFFFFF');
      });

      rows.forEach(row => {
        const rowData = {};
        activeColumns.forEach(col => {
          rowData[col.key] = col.getValue(row);
        });

        const excelRow = sheet.addRow(rowData);

        const descVal = String(rowData.descripcion || '');
        const conceptoVal = String(rowData.concepto || '');
        const lineCount = Math.max(
          conceptoVal.split('\n').length || 1,
          descVal.split('\n').length || 1
        );
        excelRow.height = Math.max(20, Math.min(90, lineCount * 16));

        activeColumns.forEach((col, idx) => {
          const cell = excelRow.getCell(col.key);
          cell.font = { size: 9, bold: false };
          cell.alignment = {
            vertical: 'middle',
            horizontal: col.align || 'left',
            wrapText: Boolean(col.wrap)
          };
          applyThinBorder(cell, 'D9E2F3');

          if (col.isMoney) {
            cell.numFmt = '"$"#,##0.00';
            cell.alignment = { horizontal: 'right', vertical: 'middle' };
          }
          if (col.isAuth) {
            cell.font = { size: 14, bold: true, color: { argb: '0070C0' } };
          }
        });
      });
    };

    if (!modoPorCC) {
      // MODO ESTÁNDAR (1 Hoja General)
      const sheet1 = workbook.addWorksheet('Ordenes de compra');
      renderOrdenesSheet(sheet1, seleccionadas);
    } else {
      // MODO DIVIDIDO POR CENTRO DE COSTO (1 Hoja por cada Grupo de CC)
      const porCC = seleccionadas.reduce((acc, row) => {
        const ccGrupo = getGrupoCentroCosto(row);
        if (!acc[ccGrupo]) acc[ccGrupo] = [];
        acc[ccGrupo].push(row);
        return acc;
      }, {});

      const usedSheetNames = new Set();
      Object.keys(porCC).sort().forEach((cc, idx) => {
        let sheetName = sanitizeSheetName(cc, `CC_${idx + 1}`);
        let suffix = 1;
        while (usedSheetNames.has(sheetName.toLowerCase())) {
          sheetName = sanitizeSheetName(`${cc.substring(0, 27)}_${suffix++}`, `CC_${idx + 1}`);
        }
        usedSheetNames.add(sheetName.toLowerCase());

        const sheetCC = workbook.addWorksheet(sheetName);
        renderOrdenesSheet(sheetCC, porCC[cc]);
      });
    }

    // Detección dinámica de todas las monedas presentes en la selección
    const rawCurrencies = Array.from(
      new Set(
        seleccionadas
          .map(r => String(r.moneda || 'MXN').trim().toUpperCase())
          .filter(Boolean)
      )
    );

    const standardOrder = ['MXN', 'USD', 'EUR'];
    const activeCurrencies = [];
    standardOrder.forEach(c => {
      if (rawCurrencies.includes(c)) activeCurrencies.push(c);
    });
    rawCurrencies.forEach(c => {
      if (!activeCurrencies.includes(c)) activeCurrencies.push(c);
    });
    if (activeCurrencies.length === 0) activeCurrencies.push('MXN', 'USD');

    const totalCols = 1 + activeCurrencies.length;
    const getResumenColLetter = (idx) => {
      let letter = '';
      while (idx > 0) {
        const mod = (idx - 1) % 26;
        letter = String.fromCharCode(65 + mod) + letter;
        idx = Math.floor((idx - mod) / 26);
      }
      return letter || 'A';
    };
    const lastResumenCol = getResumenColLetter(totalCols);

    const sheet2 = workbook.addWorksheet('Resumen de OC autorizadas');
    sheet2.columns = [
      { key: 'proveedor', width: 60 },
      ...activeCurrencies.map(c => ({ key: c.toLowerCase(), width: 18 })),
    ];

    sheet2.mergeCells(`A1:${lastResumenCol}1`);
    sheet2.getCell('A1').value = 'Resumen de solicitud de pago de ordenes de compra';
    sheet2.getCell('A1').font = { bold: true, size: 16, color: { argb: 'FFFFFF' } };
    sheet2.getCell('A1').alignment = { horizontal: 'center', vertical: 'middle' };
    sheet2.getCell('A1').fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: '1F3F6D' } };
    sheet2.getRow(1).height = 28;

    sheet2.mergeCells(`A2:${lastResumenCol}2`);
    sheet2.getCell('A2').value = modoPorCC ? 'Resumen general (Dividido por C.C.)' : 'O.C. autorizadas';
    sheet2.getCell('A2').font = { bold: true, italic: true, size: 13, color: { argb: 'FFFFFF' } };
    sheet2.getCell('A2').alignment = { horizontal: 'center', vertical: 'middle' };
    sheet2.getCell('A2').fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: '1F3F6D' } };
    sheet2.getRow(2).height = 24;

    sheet2.addRow({});
    const header = sheet2.addRow(['Proveedor / Orden', ...activeCurrencies]);
    header.eachCell((cell) => {
      cell.font = { bold: true, size: 12, color: { argb: 'FFFFFF' } };
      cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: '18836E' } };
      cell.alignment = { horizontal: cell.col === 1 ? 'left' : 'center', vertical: 'middle' };
    });

    const grouped = seleccionadas.reduce((acc, row) => {
      const prov = row.proveedor || 'SIN PROVEEDOR';
      if (!acc[prov]) acc[prov] = [];
      acc[prov].push(row);
      return acc;
    }, {});

    const totalesPorMoneda = {};
    activeCurrencies.forEach(c => { totalesPorMoneda[c] = 0; });

    Object.keys(grouped).sort().forEach(prov => {
      const providerRows = grouped[prov];
      const subtotales = activeCurrencies.map(c => {
        const sub = providerRows
          .filter(r => String(r.moneda || 'MXN').trim().toUpperCase() === c)
          .reduce((sum, r) => sum + (Number(r.pago) || 0), 0);
        return sub;
      });

      activeCurrencies.forEach((c, i) => {
        totalesPorMoneda[c] += subtotales[i];
      });

      const provRow = sheet2.addRow([
        prov,
        ...subtotales.map(s => (s > 0 ? s : ''))
      ]);
      provRow.height = 22;
      provRow.eachCell((cell, colNumber) => {
        cell.font = { bold: true, size: 10 };
        cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'E9F1F7' } };
        applyThinBorder(cell, 'B0C4DE');
        if (colNumber > 1) {
          cell.numFmt = '"$"#,##0.00';
          cell.alignment = { horizontal: 'right', vertical: 'middle' };
        }
      });

      providerRows.forEach(r => {
        const rMoneda = String(r.moneda || 'MXN').trim().toUpperCase();
        const itemRowVals = [
          `   - ${r.orden} (${r.empresa})`,
          ...activeCurrencies.map(c => (rMoneda === c ? (Number(r.pago) || 0) : ''))
        ];
        const itemRow = sheet2.addRow(itemRowVals);
        itemRow.height = 18;
        itemRow.eachCell((cell, colNumber) => {
          cell.font = { size: 9 };
          applyThinBorder(cell, 'E2E8F0');
          if (colNumber > 1) {
            cell.numFmt = '"$"#,##0.00';
            cell.alignment = { horizontal: 'right', vertical: 'middle' };
          }
        });
      });
    });

    const totalRow = sheet2.addRow([
      'TOTAL GENERAL',
      ...activeCurrencies.map(c => totalesPorMoneda[c] || 0)
    ]);
    totalRow.height = 24;
    totalRow.eachCell((cell, colNumber) => {
      cell.font = { bold: true, size: 11, color: { argb: 'FFFFFF' } };
      cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: '104E8B' } };
      applyThinBorder(cell, '104E8B');
      if (colNumber > 1) {
        cell.numFmt = '"$"#,##0.00';
        cell.alignment = { horizontal: 'right', vertical: 'middle' };
      }
    });

    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });
    const filename = `Reporte_Autorizadas_${modoPorCC ? 'PorCC_' : ''}${guardarEnHistorial ? 'Lote_' : ''}${formatInputDate(new Date())}.xlsx`;
    saveAs(blob, filename);

    if (guardarEnHistorial) {
      try {
        await axios.post(`${apiUrl}/reporteAutorizadas/guardarHistorial`, {
          usuario: loggedInUsername,
          archivo: filename,
          items: seleccionadas.map(buildHistorialItem),
        });
        await handleOpenSweetAlert(`Lote generado y guardado exitosamente (${seleccionadas.length} órdenes).`, 'success');
        fetchData();
      } catch (err) {
        console.error('Error al guardar historial de lote:', err);
      }
    }
  };

  return (
    <Box
      sx={{
        p: 'clamp(6px, 0.8vw, 12px)',
        height: '100%',
        boxSizing: 'border-box',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'auto',
        bgcolor: '#f8fafc',
      }}
    >
      {/* ── TOOLBAR MÓVIL (2 NIVELES ERGONÓMICOS) ── */}
      <Paper
        elevation={0}
        sx={{
          display: { xs: 'flex', md: 'none' },
          flexDirection: 'column',
          gap: 1,
          p: 1.2,
          mb: 1.2,
          borderRadius: '14px',
          border: '1px solid #e2e8f0',
          bgcolor: '#ffffff',
          boxShadow: '0 2px 6px rgba(15, 23, 42, 0.03)',
          flexShrink: 0,
        }}
      >
        {/* FILA 1: Buscador rápido + Botones de acción rápida */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8, width: '100%' }}>
          <TextField
            disabled={loading}
            size="small"
            placeholder="Búsqueda rápida..."
            value={filterData.BUSQUEDA_RAPIDA}
            onChange={(e) => updateFilter('BUSQUEDA_RAPIDA', e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') fetchData();
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start" sx={{ mr: 0.5 }}>
                  <SearchIcon sx={{ color: '#0655A5', fontSize: 16 }} />
                </InputAdornment>
              ),
              endAdornment: filterData.BUSQUEDA_RAPIDA ? (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    onClick={() => updateFilter('BUSQUEDA_RAPIDA', '')}
                    sx={{ p: 0.2, color: '#94a3b8', '&:hover': { color: '#0f172a' } }}
                  >
                    <ClearIcon sx={{ fontSize: 14 }} />
                  </IconButton>
                </InputAdornment>
              ) : null,
            }}
            sx={{
              flex: 1,
              minWidth: 0,
              '& .MuiOutlinedInput-root': {
                height: 36,
                borderRadius: '10px',
                bgcolor: '#f8fafc',
                fontSize: '0.72rem',
                fontWeight: 600,
                px: 0.8,
                '& fieldset': { borderColor: '#cbd5e1' },
                '&:hover fieldset': { borderColor: '#94a3b8' },
                '&.Mui-focused fieldset': { borderColor: '#0655A5', borderWidth: '1.5px', bgcolor: '#ffffff' },
              }
            }}
          />

          {/* Botón Jerarquía */}
          <Tooltip title="Filtro jerárquico">
            <IconButton
              size="small"
              onClick={(e) => setHierarchicalAnchorEl(e.currentTarget)}
              sx={{
                width: 36,
                height: 36,
                borderRadius: '10px',
                border: '1px solid',
                borderColor: hasHierarchicalFilter ? '#0655A5' : '#cbd5e1',
                bgcolor: hasHierarchicalFilter ? '#eff6ff' : '#ffffff',
                color: hasHierarchicalFilter ? '#0655A5' : '#64748b',
                flexShrink: 0,
                '&:hover': { bgcolor: hasHierarchicalFilter ? '#dbeafe' : '#f8fafc', borderColor: '#0655A5' },
              }}
            >
              <AccountTreeIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Tooltip>

          {/* Botón Filtros Extra con Badge */}
          <Tooltip title="Más filtros">
            <IconButton
              size="small"
              onClick={(e) => {
                setFilterAnchorEl(e.currentTarget);
                setShowFiltersPanel(true);
              }}
              sx={{
                width: 36,
                height: 36,
                borderRadius: '10px',
                border: '1px solid',
                borderColor: extraFiltersCount > 0 ? '#0655A5' : '#cbd5e1',
                bgcolor: extraFiltersCount > 0 ? '#eff6ff' : '#ffffff',
                color: extraFiltersCount > 0 ? '#0655A5' : '#64748b',
                flexShrink: 0,
                '&:hover': { bgcolor: extraFiltersCount > 0 ? '#dbeafe' : '#f8fafc', borderColor: '#0655A5' },
              }}
            >
              <Badge
                badgeContent={extraFiltersCount}
                color="primary"
                sx={{
                  '& .MuiBadge-badge': {
                    fontSize: '0.60rem',
                    height: 16,
                    minWidth: 16,
                    top: -2,
                    right: -2,
                    bgcolor: '#0655A5',
                  }
                }}
              >
                <FilterListIcon sx={{ fontSize: 18 }} />
              </Badge>
            </IconButton>
          </Tooltip>

          {/* Botón Menú Excel */}
          <Tooltip title="Exportar Excel">
            <IconButton
              size="small"
              onClick={(e) => setMobileExcelAnchorEl(e.currentTarget)}
              disabled={loading || !preparedRows.length}
              sx={{
                width: 36,
                height: 36,
                borderRadius: '10px',
                border: '1px solid #bbf7d0',
                bgcolor: '#f0fdf4',
                color: '#16a34a',
                flexShrink: 0,
                '&:hover': { bgcolor: '#dcfce7', borderColor: '#16a34a' },
              }}
            >
              <FileDownloadIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Tooltip>
          <Menu
            anchorEl={mobileExcelAnchorEl}
            open={Boolean(mobileExcelAnchorEl)}
            onClose={() => setMobileExcelAnchorEl(null)}
            slotProps={{
              paper: {
                sx: {
                  borderRadius: '12px',
                  boxShadow: '0 10px 25px -5px rgba(0,0,0,0.15)',
                  border: '1px solid #e2e8f0',
                  py: 0.5,
                }
              }
            }}
          >
            <MenuItem
              onClick={() => {
                setMobileExcelAnchorEl(null);
                generarExcel(false);
              }}
              sx={{ fontSize: '0.78rem', fontWeight: 600, color: '#0284c7', gap: 1 }}
            >
              <FileDownloadIcon sx={{ fontSize: 18 }} />
              Descargar Consulta
            </MenuItem>
            <MenuItem
              onClick={() => {
                setMobileExcelAnchorEl(null);
                generarExcel(true);
              }}
              sx={{ fontSize: '0.78rem', fontWeight: 700, color: '#16a34a', gap: 1 }}
            >
              <FileDownloadIcon sx={{ fontSize: 18 }} />
              Solicitar a Pago (Lote)
            </MenuItem>
          </Menu>

          {/* Botón Columnas */}
          <Tooltip title="Configurar columnas">
            <IconButton
              size="small"
              onClick={() => setColumnasModalOpen(true)}
              sx={{
                width: 36,
                height: 36,
                borderRadius: '10px',
                border: '1px solid #cbd5e1',
                bgcolor: '#ffffff',
                color: '#0655A5',
                flexShrink: 0,
                '&:hover': { bgcolor: '#f0f7ff', borderColor: '#0655A5' },
              }}
            >
              <ViewColumnIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Tooltip>
        </Box>

        {/* FILA 2: Rango Fechas + Tabs Chips + Botón Buscar + Restablecer */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.8,
            overflowX: 'auto',
            pb: 0.2,
            '&::-webkit-scrollbar': { display: 'none' },
          }}
        >
          {/* Rango de Fechas Cápsula */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.4,
              bgcolor: '#f8fafc',
              px: 0.9,
              py: 0.2,
              borderRadius: '8px',
              border: '1px solid #cbd5e1',
              height: 30,
              flexShrink: 0,
            }}
          >
            <CalendarTodayIcon sx={{ fontSize: 13, color: '#64748b' }} />
            <Typography sx={{ fontSize: '0.64rem', fontWeight: 700, color: '#64748b' }}>Del</Typography>
            <TextField
              disabled={loading}
              size="small"
              type="date"
              value={filterData.FECHA_INICIO}
              onChange={(e) => updateFilter('FECHA_INICIO', e.target.value)}
              variant="standard"
              InputProps={{ disableUnderline: true }}
              sx={{ width: 92, '& .MuiInputBase-input': { fontSize: '0.64rem', fontWeight: 600, color: '#0f172a', py: 0 } }}
            />
            <Typography sx={{ fontSize: '0.64rem', fontWeight: 700, color: '#64748b' }}>al</Typography>
            <TextField
              disabled={loading}
              size="small"
              type="date"
              value={filterData.FECHA_FIN}
              onChange={(e) => updateFilter('FECHA_FIN', e.target.value)}
              variant="standard"
              InputProps={{ disableUnderline: true }}
              sx={{ width: 92, '& .MuiInputBase-input': { fontSize: '0.64rem', fontWeight: 600, color: '#0f172a', py: 0 } }}
            />
          </Box>

          {/* Tab Chip Listas */}
          <Chip
            size="small"
            icon={<CheckCircleIcon sx={{ fontSize: '14px !important', color: activeTab === TAB_LISTAS ? '#ffffff !important' : '#16a34a !important' }} />}
            label={`Listas (${readyRows.length})`}
            onClick={() => setActiveTab(TAB_LISTAS)}
            sx={{
              height: 30,
              borderRadius: '8px',
              fontSize: '0.68rem',
              fontWeight: 800,
              cursor: 'pointer',
              bgcolor: activeTab === TAB_LISTAS ? '#0655A5' : '#ffffff',
              color: activeTab === TAB_LISTAS ? '#ffffff' : '#475569',
              border: `1px solid ${activeTab === TAB_LISTAS ? '#0655A5' : '#cbd5e1'}`,
              flexShrink: 0,
              '&:hover': { bgcolor: activeTab === TAB_LISTAS ? '#054785' : '#f1f5f9' },
            }}
          />

          {/* Tab Chip Pendientes */}
          <Chip
            size="small"
            icon={<HourglassEmptyIcon sx={{ fontSize: '14px !important', color: activeTab === TAB_PENDIENTES ? '#ffffff !important' : '#d97706 !important' }} />}
            label={`Pendientes (${pendingRows.length})`}
            onClick={() => setActiveTab(TAB_PENDIENTES)}
            sx={{
              height: 30,
              borderRadius: '8px',
              fontSize: '0.68rem',
              fontWeight: 800,
              cursor: 'pointer',
              bgcolor: activeTab === TAB_PENDIENTES ? '#0655A5' : '#ffffff',
              color: activeTab === TAB_PENDIENTES ? '#ffffff' : '#475569',
              border: `1px solid ${activeTab === TAB_PENDIENTES ? '#0655A5' : '#cbd5e1'}`,
              flexShrink: 0,
              '&:hover': { bgcolor: activeTab === TAB_PENDIENTES ? '#054785' : '#f1f5f9' },
            }}
          />

          {/* Botón Buscar */}
          <Button
            variant="contained"
            size="small"
            onClick={fetchData}
            disabled={loading}
            startIcon={<SearchIcon sx={{ fontSize: '13px !important' }} />}
            sx={{
              height: 30,
              px: 1.2,
              borderRadius: '8px',
              fontSize: '0.66rem',
              fontWeight: 800,
              textTransform: 'none',
              backgroundColor: '#0655A5',
              boxShadow: 'none',
              flexShrink: 0,
              '&:hover': { backgroundColor: '#054785', boxShadow: 'none' }
            }}
          >
            Buscar
          </Button>

          {/* Botón Restablecer si hay filtros activos */}
          {hasAnyActiveFilter && (
            <Button
              variant="outlined"
              size="small"
              onClick={() => {
                setFilterData(baseFilters);
                setPage(0);
              }}
              disabled={loading}
              startIcon={<CloseIcon sx={{ fontSize: '13px !important' }} />}
              sx={{
                height: 30,
                px: 1,
                borderRadius: '8px',
                fontSize: '0.66rem',
                fontWeight: 700,
                textTransform: 'none',
                borderColor: '#fca5a5',
                color: '#dc2626',
                bgcolor: 'rgba(239, 68, 68, 0.04)',
                flexShrink: 0,
                '&:hover': { borderColor: '#ef4444', bgcolor: 'rgba(239, 68, 68, 0.1)' }
              }}
            >
              Limpiar
            </Button>
          )}
        </Box>
      </Paper>

      {/* ── CONTENEDOR UNIFICADO: PESTAÑAS SUPERIORES + BARRA DE FILTROS INFERIOR (ESCRITORIO) ── */}
      <Paper
        elevation={0}
        sx={{
          mb: 'clamp(6px, 0.6vw, 8px)',
          p: 'clamp(6px, 0.8vw, 12px)',
          borderRadius: '16px',
          border: '1px solid #e2e8f0',
          bgcolor: '#ffffff',
          boxShadow: '0 2px 6px rgba(15, 23, 42, 0.03)',
          display: { xs: 'none', md: 'flex' },
          flexDirection: 'column',
          gap: 'clamp(6px, 0.8vw, 10px)',
          flexShrink: 0,
        }}
      >
        {/* FILA SUPERIOR: TABS DE NAVEGACIÓN COMPACTOS + BOTÓN COLUMNAS VISIBLE */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: '1px solid #f1f5f9', pb: '2px', flexWrap: 'wrap', gap: 0.8 }}>
          <Tabs
            value={activeTab}
            onChange={(_, val) => setActiveTab(val)}
            textColor="primary"
            indicatorColor="primary"
            sx={{
              minHeight: '26px',
              '& .MuiTabs-indicator': {
                height: 2.5,
                borderRadius: '2px 2px 0 0',
                backgroundColor: '#0655A5',
              },
              '& .MuiTab-root': {
                minHeight: '26px',
                py: 0.2,
                px: 'clamp(8px, 1vw, 12px)',
                fontSize: 'clamp(0.64rem, 0.60rem + 0.06vw, 0.72rem)',
                fontWeight: 700,
                textTransform: 'none',
                color: '#64748b',
                borderRadius: '6px',
                transition: 'all 0.15s ease',
                '&:hover': {
                  color: '#0655A5',
                  bgcolor: '#f8fafc',
                },
                '&.Mui-selected': {
                  color: '#0655A5',
                  fontWeight: 800,
                }
              }
            }}
          >
            <Tab
              value={TAB_LISTAS}
              icon={<CheckCircleIcon sx={{ fontSize: '14px !important' }} />}
              iconPosition="start"
              label={`Listas para pago (${readyRows.length})`}
            />
            <Tab
              value={TAB_PENDIENTES}
              icon={<HourglassEmptyIcon sx={{ fontSize: '14px !important' }} />}
              iconPosition="start"
              label={`Pendientes de completar (${pendingRows.length})`}
            />
          </Tabs>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
            {!isReadyTab && (
              <Chip
                size="small"
                variant="outlined"
                color="warning"
                label="Solo seguimiento: no se exportan para pago"
                sx={{ fontWeight: 700, fontSize: '0.58rem', height: 20, borderRadius: '6px' }}
              />
            )}
            <Tooltip title="Configurar y organizar columnas visibles">
              <Button
                size="small"
                variant="outlined"
                onClick={() => setColumnasModalOpen(true)}
                startIcon={<ViewColumnIcon sx={{ fontSize: '14px !important', color: '#0655A5' }} />}
                sx={{
                  height: '24px',
                  px: 1.2,
                  borderRadius: '6px',
                  fontSize: '0.62rem',
                  fontWeight: 800,
                  textTransform: 'none',
                  borderColor: '#cbd5e1',
                  color: '#1e293b',
                  bgcolor: '#f8fafc',
                  boxShadow: '0 1px 2px rgba(0,0,0,0.03)',
                  transition: 'all 0.15s ease',
                  '&:hover': {
                    borderColor: '#0655A5',
                    color: '#0655A5',
                    bgcolor: '#f0f7ff',
                  }
                }}
              >
                Columnas
              </Button>
            </Tooltip>
          </Box>
        </Box>

        {/* FILA INFERIOR: BARRA DE FILTROS FLUIDA */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 'clamp(4px, 0.5vw, 7px)',
            flexWrap: 'wrap',
            pt: 0.2,
          }}
        >
          {/* 1. Rango de Fechas Integrado Estilo Cápsula */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4, bgcolor: '#ffffff', px: 0.8, py: 0.1, borderRadius: '10px', border: '1px solid #cbd5e1', height: 'clamp(26px, 2vw, 28px)', flexShrink: 0 }}>
            <Typography sx={{ fontSize: 'clamp(0.62rem, 0.58rem + 0.06vw, 0.68rem)', fontWeight: 700, color: '#64748b' }}>Del</Typography>
            <TextField
              disabled={loading}
              size="small"
              type="date"
              value={filterData.FECHA_INICIO}
              onChange={(e) => updateFilter('FECHA_INICIO', e.target.value)}
              variant="standard"
              InputProps={{ disableUnderline: true }}
              sx={{ width: 96, '& .MuiInputBase-input': { fontSize: 'clamp(0.62rem, 0.58rem + 0.06vw, 0.68rem)', fontWeight: 600, color: '#0f172a', py: 0 } }}
            />
            <Typography sx={{ fontSize: 'clamp(0.62rem, 0.58rem + 0.06vw, 0.68rem)', fontWeight: 700, color: '#64748b' }}>al</Typography>
            <TextField
              disabled={loading}
              size="small"
              type="date"
              value={filterData.FECHA_FIN}
              onChange={(e) => updateFilter('FECHA_FIN', e.target.value)}
              variant="standard"
              InputProps={{ disableUnderline: true }}
              sx={{ width: 96, '& .MuiInputBase-input': { fontSize: 'clamp(0.62rem, 0.58rem + 0.06vw, 0.68rem)', fontWeight: 600, color: '#0f172a', py: 0 } }}
            />
          </Box>

          {/* ── MODO COMPACTO RESPONSIVO (PANTALLAS MEDIANAS/CHICAS: xs..xl) ── */}
          <Box sx={{ display: { xs: 'flex', xl: 'none' }, alignItems: 'center', gap: 0.8, flex: 1, minWidth: 0 }}>
            {/* Botón Popover: Jerarquía Completa (Empresa > Proyecto > Grupo > CC) */}
            <Button
              size="small"
              variant="outlined"
              onClick={(e) => setHierarchicalAnchorEl(e.currentTarget)}
              startIcon={<AccountTreeIcon sx={{ color: hasHierarchicalFilter ? '#0655A5' : '#64748b', fontSize: 16 }} />}
              endIcon={<ExpandMoreIcon sx={{ fontSize: 14, color: '#64748b' }} />}
              sx={{
                height: 28,
                px: 1.2,
                borderRadius: '10px',
                textTransform: 'none',
                borderColor: hasHierarchicalFilter ? '#60a5fa' : '#cbd5e1',
                bgcolor: hasHierarchicalFilter ? '#f0f7ff' : '#ffffff',
                color: hasHierarchicalFilter ? '#0655A5' : '#475569',
                fontWeight: hasHierarchicalFilter ? 700 : 500,
                fontSize: '0.68rem',
                maxWidth: 320,
                overflow: 'hidden',
                whiteSpace: 'nowrap',
                textOverflow: 'ellipsis',
                flexShrink: 0,
                '&:hover': {
                  borderColor: '#0655A5',
                  bgcolor: hasHierarchicalFilter ? '#e0f2fe' : '#f8fafc',
                }
              }}
            >
              <Box component="span" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {hierarchicalSummary}
              </Box>
            </Button>

            {/* Botón Popover: Filtros Complementarios (Depto, Proveedor, Divisa) */}
            <Button
              size="small"
              variant="outlined"
              onClick={(e) => {
                setFilterAnchorEl(e.currentTarget);
                setShowFiltersPanel(true);
              }}
              startIcon={<FilterListIcon sx={{ color: (filterData.DEPARTAMENTOS?.length || filterData.PROVEEDORES?.length || filterData.DIVISAS?.length) ? '#0655A5' : '#64748b', fontSize: 15 }} />}
              endIcon={<ExpandMoreIcon sx={{ fontSize: 14, color: '#64748b' }} />}
              sx={{
                height: 28,
                px: 1,
                borderRadius: '10px',
                textTransform: 'none',
                borderColor: (filterData.DEPARTAMENTOS?.length || filterData.PROVEEDORES?.length || filterData.DIVISAS?.length) ? '#60a5fa' : '#cbd5e1',
                bgcolor: (filterData.DEPARTAMENTOS?.length || filterData.PROVEEDORES?.length || filterData.DIVISAS?.length) ? '#f0f7ff' : '#ffffff',
                color: (filterData.DEPARTAMENTOS?.length || filterData.PROVEEDORES?.length || filterData.DIVISAS?.length) ? '#0655A5' : '#475569',
                fontWeight: (filterData.DEPARTAMENTOS?.length || filterData.PROVEEDORES?.length || filterData.DIVISAS?.length) ? 700 : 500,
                fontSize: '0.68rem',
                flexShrink: 0,
                '&:hover': {
                  borderColor: '#0655A5',
                  bgcolor: '#f8fafc',
                }
              }}
            >
              Más
              {((filterData.DEPARTAMENTOS?.length || 0) + (filterData.PROVEEDORES?.length || 0) + (filterData.DIVISAS?.length || 0)) > 0 && (
                <Box
                  component="span"
                  sx={{
                    ml: 0.6,
                    px: 0.6,
                    py: 0.1,
                    bgcolor: '#0655A5',
                    color: '#ffffff',
                    borderRadius: '10px',
                    fontSize: '0.60rem',
                    fontWeight: 800,
                  }}
                >
                  {(filterData.DEPARTAMENTOS?.length || 0) + (filterData.PROVEEDORES?.length || 0) + (filterData.DIVISAS?.length || 0)}
                </Box>
              )}
            </Button>
          </Box>

          {/* ── MODO COMPLETO ESCRITORIO (PANTALLAS GRANDES: xl+) ── */}
          <Box sx={{ display: { xs: 'none', xl: 'flex' }, alignItems: 'center', gap: 'clamp(4px, 0.5vw, 7px)', flex: 1 }}>
            {/* 2. Select Empresa */}
            <Autocomplete
              disabled={loading}
              multiple
              disableCloseOnSelect
              forcePopupIcon
              size="small"
              options={empresaOptions}
              value={empresaOptions.filter(empresa => (filterData.EMPRESA_IDS || []).includes(empresa.id))}
              onChange={(_, values) => {
                const updated = {
                  ...filterData,
                  EMPRESA_IDS: values.map(v => v.id),
                  EMPRESA: values.map(v => v.nombre).join(','),
                  PROYECTOS: [],
                  PROYECTO: '',
                  DIVISIONES: [],
                  DIVISION: '',
                  CENTRO_COSTOS: [],
                  CENTRO_COSTO: '',
                  DEPARTAMENTOS: [],
                  PROVEEDORES: [],
                  DIVISAS: [],
                };
                setFilterData(updated);
                setPage(0);
              }}
              getOptionLabel={(option) => option.nombre}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              limitTags={1}
              renderTags={() => null}
              renderOption={renderCheckboxOption}
              renderInput={(params) => renderSummaryInput(
                params,
                'Empresa',
                empresaOptions.filter(empresa => (filterData.EMPRESA_IDS || []).includes(empresa.id))
              )}
              sx={{ width: 'clamp(100px, 8.5vw, 125px)', flexShrink: 0, ...compactAutocompleteSx }}
            />

            {/* 3. Select Proyecto (Nivel 2) */}
            <Autocomplete
              disabled={loading || !(filterData.EMPRESA_IDS || []).length}
              multiple
              disableCloseOnSelect
              forcePopupIcon
              size="small"
              options={proyectoOptions}
              value={proyectoOptions.filter(p => (filterData.PROYECTOS || []).includes(p.id))}
              isOptionEqualToValue={(option, val) => option?.id === val?.id}
              onChange={(_, value) => {
                const updated = {
                  ...filterData,
                  PROYECTOS: (value || []).map(p => p.id),
                  PROYECTO: (value || []).map(p => p.nombre).join(','),
                  DIVISIONES: [],
                  DIVISION: '',
                  CENTRO_COSTOS: [],
                  CENTRO_COSTO: ''
                };
                setFilterData(updated);
                setPage(0);
              }}
              limitTags={1}
              renderTags={() => null}
              getOptionLabel={(option) => option?.nombre || ''}
              renderOption={renderCheckboxOption}
              renderInput={(params) => (
                renderSummaryInput(
                  params,
                  'Proyecto',
                  proyectoOptions.filter(p => (filterData.PROYECTOS || []).includes(p.id)).map(p => p.nombre)
                )
              )}
              sx={{ width: 'clamp(95px, 8vw, 118px)', flexShrink: 0, ...compactAutocompleteSx }}
            />

            {/* 4. Select Grupo / División (Nivel 3) */}
            <Autocomplete
              disabled={loading || !(filterData.EMPRESA_IDS || []).length}
              multiple
              disableCloseOnSelect
              forcePopupIcon
              size="small"
              options={divisionOptions}
              value={divisionOptions.filter(d => (filterData.DIVISIONES || []).includes(d.id))}
              isOptionEqualToValue={(option, val) => option?.id === val?.id}
              onChange={(_, value) => {
                const updated = {
                  ...filterData,
                  DIVISIONES: (value || []).map(d => d.id),
                  DIVISION: (value || []).map(d => d.nombre).join(','),
                  CENTRO_COSTOS: [],
                  CENTRO_COSTO: ''
                };
                setFilterData(updated);
                setPage(0);
              }}
              limitTags={1}
              renderTags={() => null}
              getOptionLabel={(option) => option?.nombre || ''}
              renderOption={renderCheckboxOption}
              renderInput={(params) => (
                renderSummaryInput(
                  params,
                  'Grupo',
                  divisionOptions.filter(d => (filterData.DIVISIONES || []).includes(d.id)).map(d => d.nombre)
                )
              )}
              sx={{ width: 'clamp(95px, 8vw, 115px)', flexShrink: 0, ...compactAutocompleteSx }}
            />

            {/* 5. Select Centro de Costo (Nivel 4) */}
            <Autocomplete
              disabled={loading || !(filterData.EMPRESA_IDS || []).length}
              multiple
              disableCloseOnSelect
              forcePopupIcon
              size="small"
              options={centroCostoOptions}
              value={filterData.CENTRO_COSTOS || []}
              onChange={(_, values) => updateFilter('CENTRO_COSTOS', values)}
              limitTags={1}
              renderTags={() => null}
              renderOption={renderCheckboxOption}
              renderInput={(params) => renderSummaryInput(params, 'Centro costo', filterData.CENTRO_COSTOS || [])}
              sx={{ width: 'clamp(100px, 9vw, 125px)', flexShrink: 0, ...compactAutocompleteSx }}
            />

            {/* 6. Select Departamento */}
            <Autocomplete
              disabled={loading}
              multiple
              disableCloseOnSelect
              forcePopupIcon
              size="small"
              options={departamentoOptions}
              value={filterData.DEPARTAMENTOS || []}
              onChange={(_, values) => updateFilter('DEPARTAMENTOS', values)}
              limitTags={1}
              renderTags={() => null}
              renderOption={renderCheckboxOption}
              renderInput={(params) => renderSummaryInput(params, 'Departamento', filterData.DEPARTAMENTOS || [])}
              sx={{ width: 'clamp(100px, 9vw, 125px)', flexShrink: 0, ...compactAutocompleteSx }}
            />

            {/* 7. Select Proveedor */}
            <Autocomplete
              disabled={loading}
              multiple
              disableCloseOnSelect
              forcePopupIcon
              size="small"
              options={proveedorOptions}
              value={filterData.PROVEEDORES || []}
              onChange={(_, values) => updateFilter('PROVEEDORES', values)}
              limitTags={1}
              renderTags={() => null}
              renderOption={renderCheckboxOption}
              renderInput={(params) => renderSummaryInput(params, 'Proveedor', filterData.PROVEEDORES || [])}
              sx={{ width: 'clamp(105px, 9.5vw, 130px)', flexShrink: 0, ...compactAutocompleteSx }}
            />

            {/* 8. Select Divisa */}
            <Autocomplete
              disabled={loading}
              multiple
              disableCloseOnSelect
              forcePopupIcon
              size="small"
              options={divisaOptions}
              value={filterData.DIVISAS || []}
              onChange={(_, values) => updateFilter('DIVISAS', values)}
              limitTags={1}
              renderTags={() => null}
              renderOption={renderCheckboxOption}
              renderInput={(params) => renderSummaryInput(params, 'Divisa', filterData.DIVISAS || [])}
              sx={{ width: 'clamp(75px, 6vw, 90px)', flexShrink: 0, ...compactAutocompleteSx }}
            />
          </Box>

          {/* Separador vertical */}
          <Box sx={{ borderLeft: '1px solid #cbd5e1', height: 20, mx: 0.3, flexShrink: 0 }} />

          {/* 9. Buscador General al final */}
          <TextField
            disabled={loading}
            size="small"
            placeholder="Búsqueda rápida..."
            value={filterData.BUSQUEDA_RAPIDA}
            onChange={(e) => updateFilter('BUSQUEDA_RAPIDA', e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') fetchData();
            }}
            InputProps={{
              startAdornment: (
                <Box sx={{ display: 'flex', alignItems: 'center', mr: 0.5 }}>
                  <SearchIcon sx={{ color: '#0655A5', fontSize: 'clamp(12px, 0.9vw, 14px)' }} />
                </Box>
              ),
              endAdornment: filterData.BUSQUEDA_RAPIDA ? (
                <Box
                  component="span"
                  onClick={() => updateFilter('BUSQUEDA_RAPIDA', '')}
                  sx={{ cursor: 'pointer', display: 'flex', alignItems: 'center', p: 0.1, color: '#94a3b8', '&:hover': { color: '#0f172a' } }}
                >
                  <ClearIcon sx={{ fontSize: 'clamp(10px, 0.8vw, 12px)' }} />
                </Box>
              ) : null,
            }}
            sx={{
              width: 'clamp(110px, 9vw, 135px)',
              flexShrink: 0,
              '& .MuiOutlinedInput-root': {
                height: 'clamp(26px, 2vw, 28px)',
                borderRadius: '10px',
                bgcolor: '#ffffff',
                fontSize: 'clamp(0.64rem, 0.60rem + 0.08vw, 0.70rem)',
                fontWeight: 500,
                px: 0.8,
                '& fieldset': { borderColor: '#cbd5e1' },
                '&:hover fieldset': { borderColor: '#94a3b8' },
                '&.Mui-focused fieldset': { borderColor: '#0655A5', borderWidth: '1.5px' },
              }
            }}
          />

          {/* Botones Buscar y Limpiar */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6, ml: 'auto', flexShrink: 0 }}>
            <Button
              variant="contained"
              size="small"
              onClick={fetchData}
              disabled={loading}
              startIcon={<SearchIcon sx={{ fontSize: 'clamp(12px, 0.9vw, 14px) !important' }} />}
              sx={{
                height: 'clamp(26px, 2vw, 28px)',
                px: 'clamp(10px, 1vw, 14px)',
                borderRadius: '8px',
                fontSize: 'clamp(0.64rem, 0.60rem + 0.08vw, 0.72rem)',
                fontWeight: 700,
                textTransform: 'none',
                backgroundColor: '#0655A5',
                boxShadow: 'none',
                '&:hover': {
                  backgroundColor: '#054785',
                  boxShadow: 'none'
                }
              }}
            >
              Buscar
            </Button>

            <Button
              variant="outlined"
              size="small"
              onClick={() => {
                setFilterData(baseFilters);
                setPage(0);
              }}
              disabled={loading}
              startIcon={<CloseIcon sx={{ fontSize: 'clamp(12px, 0.9vw, 14px) !important' }} />}
              sx={{
                height: 'clamp(26px, 2vw, 28px)',
                px: 'clamp(8px, 0.8vw, 12px)',
                borderRadius: '8px',
                fontSize: 'clamp(0.64rem, 0.60rem + 0.08vw, 0.72rem)',
                fontWeight: 700,
                textTransform: 'none',
                borderColor: '#fca5a5',
                color: '#dc2626',
                bgcolor: 'rgba(239, 68, 68, 0.04)',
                '&:hover': {
                  borderColor: '#ef4444',
                  bgcolor: 'rgba(239, 68, 68, 0.1)',
                }
              }}
            >
              Limpiar
            </Button>
          </Box>
        </Box>
      </Paper>

      {/* ── Popover 1: Jerarquía Completa (4 Niveles) ── */}
      <Popover
        open={Boolean(hierarchicalAnchorEl)}
        anchorEl={hierarchicalAnchorEl}
        onClose={() => setHierarchicalAnchorEl(null)}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        PaperProps={{
          elevation: 10,
          sx: {
            mt: 0.8,
            p: 2,
            borderRadius: '16px',
            border: '1px solid #e2e8f0',
            width: 320,
            maxWidth: '90vw',
            boxShadow: '0 20px 25px -5px rgba(15, 23, 42, 0.1), 0 8px 10px -6px rgba(15, 23, 42, 0.1)',
            display: 'flex',
            flexDirection: 'column',
            gap: 1.5,
          }
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1, borderBottom: '1px solid #f1f5f9' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <AccountTreeIcon sx={{ color: '#0655A5', fontSize: 18 }} />
            <Typography sx={{ fontSize: 13, fontWeight: 800, color: '#0f172a' }}>
              Filtro Jerárquico
            </Typography>
          </Box>
          <IconButton size="small" onClick={() => setHierarchicalAnchorEl(null)}>
            <ClearIcon sx={{ fontSize: 14 }} />
          </IconButton>
        </Box>

        {/* 1. Empresa */}
        <Box>
          <Typography sx={{ fontSize: 11, fontWeight: 700, color: '#64748b', mb: 0.5 }}>
            1. EMPRESA
          </Typography>
          <Autocomplete
            disabled={loading}
            multiple
            disableCloseOnSelect
            forcePopupIcon
            size="small"
            options={empresaOptions}
            value={empresaOptions.filter(empresa => (filterData.EMPRESA_IDS || []).includes(empresa.id))}
            onChange={(_, value) => {
              const updated = {
                ...filterData,
                EMPRESA_IDS: (value || []).map(empresa => empresa.id),
                EMPRESA: (value || []).map(empresa => empresa.nombre).join(','),
                PROYECTOS: [],
                PROYECTO: '',
                DIVISIONES: [],
                DIVISION: '',
                CENTRO_COSTOS: [],
                CENTRO_COSTO: '',
              };
              setFilterData(updated);
              setPage(0);
            }}
            getOptionLabel={(option) => option?.nombre || ''}
            isOptionEqualToValue={(option, val) => option?.id === val?.id}
            renderOption={renderCheckboxOption}
            renderInput={(params) => renderSummaryInput(params, 'Empresa', empresaOptions.filter(empresa => (filterData.EMPRESA_IDS || []).includes(empresa.id)).map(e => e.nombre))}
            fullWidth
          />
        </Box>

        {/* 2. Proyecto */}
        <Box>
          <Typography sx={{ fontSize: 11, fontWeight: 700, color: '#64748b', mb: 0.5 }}>
            2. PROYECTO
          </Typography>
          <Autocomplete
            disabled={loading || !(filterData.EMPRESA_IDS || []).length}
            multiple
            disableCloseOnSelect
            forcePopupIcon
            size="small"
            options={proyectoOptions}
            value={proyectoOptions.filter(p => (filterData.PROYECTOS || []).includes(p.id))}
            onChange={(_, value) => {
              const updated = {
                ...filterData,
                PROYECTOS: (value || []).map(p => p.id),
                PROYECTO: (value || []).map(p => p.nombre).join(','),
                DIVISIONES: [],
                DIVISION: '',
                CENTRO_COSTOS: [],
                CENTRO_COSTO: '',
              };
              setFilterData(updated);
              setPage(0);
            }}
            getOptionLabel={(option) => option?.nombre || ''}
            isOptionEqualToValue={(option, val) => option?.id === val?.id}
            renderOption={renderCheckboxOption}
            renderInput={(params) => renderSummaryInput(params, 'Proyecto', proyectoOptions.filter(p => (filterData.PROYECTOS || []).includes(p.id)).map(p => p.nombre))}
            fullWidth
          />
        </Box>

        {/* 3. Grupo / División */}
        <Box>
          <Typography sx={{ fontSize: 11, fontWeight: 700, color: '#64748b', mb: 0.5 }}>
            3. GRUPO / DIVISIÓN
          </Typography>
          <Autocomplete
            disabled={loading || !(filterData.EMPRESA_IDS || []).length}
            multiple
            disableCloseOnSelect
            forcePopupIcon
            size="small"
            options={divisionOptions}
            value={divisionOptions.filter(d => (filterData.DIVISIONES || []).includes(d.id))}
            onChange={(_, value) => {
              const updated = {
                ...filterData,
                DIVISIONES: (value || []).map(d => d.id),
                DIVISION: (value || []).map(d => d.nombre).join(','),
                CENTRO_COSTOS: [],
                CENTRO_COSTO: '',
              };
              setFilterData(updated);
              setPage(0);
            }}
            getOptionLabel={(option) => option?.nombre || ''}
            isOptionEqualToValue={(option, val) => option?.id === val?.id}
            renderOption={renderCheckboxOption}
            renderInput={(params) => renderSummaryInput(params, 'Grupo / División', divisionOptions.filter(d => (filterData.DIVISIONES || []).includes(d.id)).map(d => d.nombre))}
            fullWidth
          />
        </Box>

        {/* 4. Centro de Costo */}
        <Box>
          <Typography sx={{ fontSize: 11, fontWeight: 700, color: '#64748b', mb: 0.5 }}>
            4. CENTRO DE COSTO
          </Typography>
          <Autocomplete
            disabled={loading || !(filterData.EMPRESA_IDS || []).length}
            multiple
            disableCloseOnSelect
            forcePopupIcon
            size="small"
            options={centroCostoOptions}
            value={filterData.CENTRO_COSTOS || []}
            onChange={(_, value) => {
              updateFilter('CENTRO_COSTOS', value || []);
            }}
            renderOption={renderCheckboxOption}
            renderInput={(params) => renderSummaryInput(params, 'Centro costo', filterData.CENTRO_COSTOS || [])}
            fullWidth
          />
        </Box>

        <Stack direction="row" spacing={1} justifyContent="flex-end" sx={{ mt: 0.5, pt: 1, borderTop: '1px solid #f1f5f9' }}>
          <Button
            size="small"
            variant="outlined"
            onClick={() => {
              const cleared = {
                ...filterData,
                EMPRESA_IDS: [],
                EMPRESA: '',
                PROYECTOS: [],
                PROYECTO: '',
                DIVISIONES: [],
                DIVISION: '',
                CENTRO_COSTOS: [],
                CENTRO_COSTO: '',
              };
              setFilterData(cleared);
              setHierarchicalAnchorEl(null);
              setPage(0);
            }}
            sx={{ fontSize: 11, textTransform: 'none', borderRadius: '8px' }}
          >
            Limpiar Jerarquía
          </Button>
          <Button
            size="small"
            variant="contained"
            onClick={() => {
              setHierarchicalAnchorEl(null);
            }}
            sx={{ fontSize: 11, textTransform: 'none', bgcolor: '#0655A5', borderRadius: '8px', '&:hover': { bgcolor: '#044384' } }}
          >
            Cerrar
          </Button>
        </Stack>
      </Popover>

      {/* ── Popover 2: Filtros Complementarios / Más Filtros ── */}
      <Popover
        open={showFiltersPanel}
        anchorEl={filterAnchorEl}
        onClose={() => setShowFiltersPanel(false)}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        PaperProps={{
          elevation: 10,
          sx: {
            mt: 0.8,
            p: 2,
            borderRadius: '16px',
            border: '1px solid #e2e8f0',
            width: 320,
            maxWidth: '90vw',
            boxShadow: '0 20px 25px -5px rgba(15, 23, 42, 0.1), 0 8px 10px -6px rgba(15, 23, 42, 0.1)',
            display: 'flex',
            flexDirection: 'column',
            gap: 1.5,
          }
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1, borderBottom: '1px solid #f1f5f9' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <FilterListIcon sx={{ color: '#0655A5', fontSize: 18 }} />
            <Typography sx={{ fontSize: 13, fontWeight: 800, color: '#0f172a' }}>
              Más Filtros
            </Typography>
          </Box>
          <IconButton size="small" onClick={() => setShowFiltersPanel(false)}>
            <ClearIcon sx={{ fontSize: 14 }} />
          </IconButton>
        </Box>

        {/* 1. Departamento */}
        <Box>
          <Typography sx={{ fontSize: 11, fontWeight: 700, color: '#64748b', mb: 0.5 }}>
            DEPARTAMENTO
          </Typography>
          <Autocomplete
            disabled={loading}
            multiple
            disableCloseOnSelect
            forcePopupIcon
            size="small"
            options={departamentoOptions}
            value={filterData.DEPARTAMENTOS || []}
            onChange={(_, value) => updateFilter('DEPARTAMENTOS', value || [])}
            renderOption={renderCheckboxOption}
            renderInput={(params) => renderSummaryInput(params, 'Departamento', filterData.DEPARTAMENTOS || [])}
            fullWidth
          />
        </Box>

        {/* 2. Proveedor */}
        <Box>
          <Typography sx={{ fontSize: 11, fontWeight: 700, color: '#64748b', mb: 0.5 }}>
            PROVEEDOR
          </Typography>
          <Autocomplete
            disabled={loading}
            multiple
            disableCloseOnSelect
            forcePopupIcon
            size="small"
            options={proveedorOptions}
            value={filterData.PROVEEDORES || []}
            onChange={(_, value) => updateFilter('PROVEEDORES', value || [])}
            renderOption={renderCheckboxOption}
            renderInput={(params) => renderSummaryInput(params, 'Proveedor', filterData.PROVEEDORES || [])}
            fullWidth
          />
        </Box>

        {/* 3. Divisa */}
        <Box>
          <Typography sx={{ fontSize: 11, fontWeight: 700, color: '#64748b', mb: 0.5 }}>
            DIVISA
          </Typography>
          <Autocomplete
            disabled={loading}
            multiple
            disableCloseOnSelect
            forcePopupIcon
            size="small"
            options={divisaOptions}
            value={filterData.DIVISAS || []}
            onChange={(_, value) => updateFilter('DIVISAS', value || [])}
            renderOption={renderCheckboxOption}
            renderInput={(params) => renderSummaryInput(params, 'Divisa', filterData.DIVISAS || [])}
            fullWidth
          />
        </Box>
      </Popover>

      {/* ── BLOQUE DE RESUMEN Y ACCIONES / EXPORTACIÓN (REDiseño PREMIUM) ── */}
      <Paper
        elevation={0}
        sx={{
          mb: 'clamp(6px, 0.6vw, 8px)',
          p: 'clamp(5px, 0.5vw, 8px)',
          px: 'clamp(8px, 0.9vw, 14px)',
          display: { xs: 'none', md: 'flex' },
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: 1,
          bgcolor: '#ffffff',
          borderRadius: '14px',
          border: '1px solid #e2e8f0',
          boxShadow: '0 2px 6px rgba(15, 23, 42, 0.03)',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 'clamp(8px, 1vw, 16px)', flexWrap: 'wrap' }}>
          {/* Conteo de órdenes */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6 }}>
            <Typography sx={{ fontWeight: 800, fontSize: 'clamp(0.68rem, 0.64rem + 0.08vw, 0.78rem)', color: '#0f172a' }}>
              Órdenes: <strong style={{ color: '#0655A5' }}>{preparedRows.length}</strong>
            </Typography>
          </Box>

          {/* Totales visibles de la tabla por divisa */}
          {visibleTotals.length > 0 && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8, pl: 1, borderLeft: '1.5px solid #e2e8f0' }}>
              <Typography sx={{ fontSize: 'clamp(0.62rem, 0.58rem + 0.06vw, 0.70rem)', fontWeight: 700, color: '#64748b' }}>
                Total mostradas:
              </Typography>
              {visibleTotals.map(([moneda, total]) => (
                <Chip
                  key={moneda}
                  size="small"
                  label={`${moneda}: ${formatCurrency(total, moneda)}`}
                  sx={{
                    height: 'clamp(20px, 1.6vw, 24px)',
                    fontSize: 'clamp(0.62rem, 0.58rem + 0.06vw, 0.70rem)',
                    fontWeight: 800,
                    bgcolor: '#f1f5f9',
                    color: moneda === 'USD' ? '#b45309' : moneda === 'EUR' ? '#4338ca' : '#0369a1',
                    border: '1px solid #e2e8f0',
                    borderRadius: '8px',
                  }}
                />
              ))}
            </Box>
          )}

          {/* Totales de órdenes seleccionadas por divisa */}
          {selectedCount > 0 && selectedTotals.length > 0 && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8, pl: 1, borderLeft: '1.5px solid #bfdbfe' }}>
              <Chip
                label={`${selectedCount} seleccionadas`}
                size="small"
                sx={{
                  height: 'clamp(20px, 1.6vw, 24px)',
                  fontSize: 'clamp(0.62rem, 0.58rem + 0.06vw, 0.70rem)',
                  fontWeight: 800,
                  bgcolor: '#0655A5',
                  color: '#ffffff',
                  borderRadius: '8px',
                }}
              />
              <Typography sx={{ fontSize: 'clamp(0.62rem, 0.58rem + 0.06vw, 0.70rem)', fontWeight: 700, color: '#1e40af' }}>
                Selección:
              </Typography>
              {selectedTotals.map(([moneda, total]) => (
                <Chip
                  key={moneda}
                  size="small"
                  label={`${moneda}: ${formatCurrency(total, moneda)}`}
                  sx={{
                    height: 'clamp(20px, 1.6vw, 24px)',
                    fontSize: 'clamp(0.62rem, 0.58rem + 0.06vw, 0.70rem)',
                    fontWeight: 800,
                    bgcolor: '#eff6ff',
                    color: moneda === 'USD' ? '#d97706' : moneda === 'EUR' ? '#4f46e5' : '#0284c7',
                    border: '1px solid #bfdbfe',
                    borderRadius: '8px',
                  }}
                />
              ))}
            </Box>
          )}
        </Box>

        <Stack direction="row" spacing={0.8} alignItems="center" flexWrap="wrap">
          <Button
            size="small"
            variant="outlined"
            onClick={() => generarExcel(false)}
            disabled={loading || !preparedRows.length}
            startIcon={<FileDownloadIcon sx={{ fontSize: 'clamp(12px, 0.9vw, 14px) !important' }} />}
            sx={{
              height: 'clamp(26px, 2vw, 28px)',
              px: 'clamp(10px, 1vw, 14px)',
              fontSize: 'clamp(0.64rem, 0.60rem + 0.08vw, 0.72rem)',
              textTransform: 'none',
              borderRadius: '8px',
              fontWeight: 700,
              borderColor: '#cbd5e1',
              color: '#0284c7',
              bgcolor: '#f0f9ff',
              '&:hover': { bgcolor: '#e0f2fe', borderColor: '#0284c7' }
            }}
          >
            Consulta
          </Button>

          <Button
            size="small"
            variant="contained"
            onClick={() => generarExcel(true)}
            disabled={loading || !preparedRows.length}
            startIcon={<FileDownloadIcon sx={{ fontSize: 'clamp(12px, 0.9vw, 14px) !important' }} />}
            sx={{
              height: 'clamp(26px, 2vw, 28px)',
              px: 'clamp(12px, 1.1vw, 16px)',
              fontSize: 'clamp(0.64rem, 0.60rem + 0.08vw, 0.72rem)',
              textTransform: 'none',
              borderRadius: '8px',
              fontWeight: 800,
              bgcolor: '#16a34a',
              color: '#ffffff',
              boxShadow: 'none',
              '&:hover': { bgcolor: '#15803d' }
            }}
          >
            Solicitar a Pago
          </Button>
        </Stack>
      </Paper>

      <Paper
        elevation={0}
        sx={{
          flex: '1 1 auto',
          minHeight: 0,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          borderRadius: '10px',
          border: '1px solid #e2e8f0',
          boxShadow: '0 1px 4px rgba(15, 23, 42, 0.03)',
          backgroundColor: '#ffffff',
        }}
      >
        {/* ── LISTA DE TARJETAS MÓVILES (VISTA MÓVIL) ── */}
        <Box
          onScroll={handleContainerScroll}
          sx={{
            display: { xs: 'block', md: 'none' },
            flex: 1,
            minHeight: 0,
            overflowY: 'auto',
            p: 1.2,
            pb: '80px',
            bgcolor: '#f8fafc',
          }}
        >
          {loading ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', py: 6, gap: 1.5 }}>
              <CircularProgress size={32} sx={{ color: '#0655A5' }} />
              <Typography sx={{ fontSize: '0.74rem', color: '#64748b', fontWeight: 600 }}>
                Cargando solicitudes autorizadas...
              </Typography>
            </Box>
          ) : preparedRows.length === 0 ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', py: 8, px: 2, textAlign: 'center' }}>
              <Typography sx={{ fontSize: '0.80rem', fontWeight: 700, color: '#64748b', fontStyle: 'italic' }}>
                {isReadyTab
                  ? 'No hay solicitudes autorizadas listas para pago.'
                  : 'No hay solicitudes pendientes de completar.'}
              </Typography>
            </Box>
          ) : (
            <>
              {displayedRows.map((row) => (
                <ReporteMobileCard
                  key={row.ID}
                  row={row}
                  autorizar={!!autorizarChecked[row.ID]}
                  onAutorizar={toggleAutorizar}
                  selectable={isReadyTab}
                  isReadyTab={isReadyTab}
                />
              ))}
            </>
          )}
        </Box>

        <TableContainer
          onScroll={handleContainerScroll}
          sx={{ display: { xs: 'none', md: 'block' }, flex: 1, minHeight: 0, overflow: 'auto' }}
        >
          <Table
            stickyHeader
            size="small"
            sx={{
              '& .MuiTableCell-head': {
                backgroundColor: '#0655A5',
                backgroundImage: 'linear-gradient(180deg, #075ea6 0%, #034078 100%)',
                color: '#ffffff',
                fontWeight: 800,
                fontSize: 'clamp(0.60rem, 0.58rem + 0.05vw, 0.68rem)',
                letterSpacing: 0,
                borderBottom: '1px solid rgba(255,255,255,0.12)',
                whiteSpace: 'nowrap',
                py: 'clamp(5px, 0.6vw, 8px)',
                px: 'clamp(6px, 0.7vw, 10px)',
              },
              '& .MuiTableCell-body': {
                borderBottom: '1px solid #eef2f7',
                fontSize: 'clamp(0.62rem, 0.58rem + 0.1vw, 0.74rem)',
                py: 'clamp(4px, 0.45vw, 6px)',
                px: 'clamp(6px, 0.7vw, 10px)',
              },
              '& .MuiTableRow-root:nth-of-type(even) .MuiTableCell-body': {
                backgroundColor: '#fcfdfe',
              },
              '& .MuiTableRow-root:hover .MuiTableCell-body': {
                backgroundColor: '#f1f5f9',
              },
              '& .MuiTableRow-root.Mui-selected .MuiTableCell-body': {
                backgroundColor: '#eaf4ff',
              },
              '& .MuiTableRow-root.Mui-selected:hover .MuiTableCell-body': {
                backgroundColor: '#dceeff',
              },
            }}
          >
            <TableHead>
              <TableRow>
                {columnOrder.map(colId => {
                  if (visibleCols[colId] === false) return null;
                  const def = ALL_COLUMNS_DEF.find(c => c.id === colId);
                  const label = def?.label || colId.toUpperCase();
                  const isRight = colId === 'pago' || colId === 'totalMn' || colId === 'totalUsd' || colId === 'totalEur';
                  const isCenter = colId === 'accion' || colId === 'diasSinPago' || colId === 'fechaCompromiso';
                  const displayLabel = colId === 'accion' ? (isReadyTab ? 'AUTORIZAR' : 'ESTATUS') : label;

                  return (
                    <TableCell
                      key={colId}
                      align={isRight ? 'right' : isCenter ? 'center' : 'left'}
                    >
                      {displayLabel}
                    </TableCell>
                  );
                })}
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={13} align="center" sx={{ py: 3 }}>
                    <CircularProgress size={24} sx={{ color: '#0655A5' }} />
                  </TableCell>
                </TableRow>
              ) : preparedRows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={13} align="center" sx={{ py: 3, fontStyle: 'italic', color: '#94a3b8', fontSize: '0.74rem' }}>
                    {isReadyTab
                      ? 'No hay solicitudes autorizadas pendientes de pago.'
                      : 'No hay solicitudes pendientes de completar.'}
                  </TableCell>
                </TableRow>
              ) : (
                paginatedRows.map((row) => (
                  <ReporteRow
                    key={row.ID}
                    row={row}
                    autorizar={!!autorizarChecked[row.ID]}
                    onAutorizar={toggleAutorizar}
                    selectable={isReadyTab}
                    visibleCols={visibleCols}
                    columnOrder={columnOrder}
                  />
                ))
              )}
            </TableBody>
            {preparedRows.length > 0 && (() => {
              let sumTotalAll = 0;
              let sumMxn = 0;
              let sumUsd = 0;
              let sumEur = 0;
              const breakdown = {};

              const getBadgeStyle = (div) => {
                if (div === 'USD') return { color: '#b45309', bg: '#fef3c7' };
                if (div === 'EUR') return { color: '#4338ca', bg: '#e0e7ff' };
                if (div === 'MXN') return { color: '#0369a1', bg: '#e0f2fe' };
                if (div === 'GBP') return { color: '#701a75', bg: '#fae8ff' };
                if (div === 'CAD') return { color: '#991b1b', bg: '#fee2e2' };
                return { color: '#334155', bg: '#f1f5f9' };
              };

              const getCurrencySymbol = (div) => {
                if (div === 'EUR') return '€';
                if (div === 'GBP') return '£';
                return '$';
              };

              preparedRows.forEach(r => {
                const val = parseMoney(r.pago);
                const rawDiv = String(r.moneda || 'MXN').toUpperCase().trim();
                const divisa = (rawDiv === 'USD' || rawDiv === 'DOLARES' || rawDiv === 'DOLAR') ? 'USD'
                  : (rawDiv === 'EUR' || rawDiv === 'EUROS' || rawDiv === 'EURO') ? 'EUR'
                  : (rawDiv === 'GBP' || rawDiv === 'LIBRAS' || rawDiv === 'LIBRA') ? 'GBP'
                  : (rawDiv === 'CAD') ? 'CAD'
                  : rawDiv || 'MXN';

                sumTotalAll += val;
                if (divisa === 'USD') sumUsd += val;
                else if (divisa === 'EUR') sumEur += val;
                else sumMxn += val;

                if (!breakdown[divisa]) breakdown[divisa] = { total: 0, count: 0 };
                breakdown[divisa].total += val;
                breakdown[divisa].count += 1;
              });

              breakdownRef.current = breakdown;

              const divisasEntries = Object.entries(breakdown).sort((a, b) => {
                const order = { MXN: 1, USD: 2, EUR: 3 };
                return (order[a[0]] || 99) - (order[b[0]] || 99);
              });

              return (
                <tfoot
                  style={{ position: 'sticky', bottom: 0, zIndex: 12, backgroundColor: '#f8fafc' }}
                  onClick={(e) => e.stopPropagation()}
                >
                  <tr style={{ backgroundColor: '#f8fafc', borderTop: '2px solid #cbd5e1', borderBottom: '1px solid #cbd5e1', boxShadow: '0 -4px 10px rgba(15, 23, 42, 0.04)' }}>
                    {columnOrder.map((colId, idx) => {
                      if (visibleCols[colId] === false) return null;
                      const isFirstCol = idx === 0 || (visibleCols[columnOrder[0]] === false && colId === columnOrder.find(c => visibleCols[c] !== false));
                      const isRight = colId === 'pago' || colId === 'totalMn' || colId === 'totalUsd' || colId === 'totalEur';
                      const isCenter = colId === 'accion' || colId === 'diasSinPago';

                      let content = null;
                      if (isFirstCol) {
                        content = (
                          <Typography sx={{ fontWeight: 850, fontSize: '0.60rem', color: '#475569', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                            TOTALES ({preparedRows.length})
                          </Typography>
                        );
                      } else if (colId === 'pago') {
                        content = (
                          <Box
                            onClick={(e) => {
                              e.stopPropagation();
                              totalesPopoverRef.current?.open(e.currentTarget);
                            }}
                            sx={{
                              display: 'flex',
                              flexDirection: 'column',
                              gap: '1.5px',
                              py: 0.1,
                              cursor: 'pointer'
                            }}
                          >
                            {divisasEntries.map(([divisa, vals]) => {
                              const total = vals.total || 0;
                              const { color: badgeColor, bg: badgeBg } = getBadgeStyle(divisa);
                              const sym = getCurrencySymbol(divisa);
                              return (
                                <Box key={divisa} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.4, lineHeight: 1 }}>
                                  <Typography sx={{ fontSize: '0.50rem', fontWeight: 800, color: badgeColor, bgcolor: badgeBg, px: 0.35, py: 0.05, borderRadius: '2px', minWidth: 20, textAlign: 'center', lineHeight: 1.1 }}>
                                    {divisa}
                                  </Typography>
                                  <Typography sx={{ fontSize: '0.66rem', fontWeight: 800, color: total > 0 ? '#0f172a' : '#94a3b8', fontVariantNumeric: 'tabular-nums', lineHeight: 1, whiteSpace: 'nowrap' }}>
                                    {sym}{total.toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                  </Typography>
                                </Box>
                              );
                            })}
                          </Box>
                        );
                      } else if (colId === 'totalMn') {
                        content = (
                          <Typography sx={{ fontWeight: 900, fontSize: '0.74rem', color: sumMxn > 0 ? '#0f172a' : '#94a3b8', textAlign: 'right', whiteSpace: 'nowrap' }}>
                            {sumMxn > 0 ? `$${sumMxn.toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : '-'}
                          </Typography>
                        );
                      } else if (colId === 'totalUsd') {
                        content = (
                          <Typography sx={{ fontWeight: 900, fontSize: '0.74rem', color: sumUsd > 0 ? '#0284c7' : '#94a3b8', textAlign: 'right', whiteSpace: 'nowrap' }}>
                            {sumUsd > 0 ? `$${sumUsd.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : '-'}
                          </Typography>
                        );
                      } else if (colId === 'totalEur') {
                        content = (
                          <Typography sx={{ fontWeight: 900, fontSize: '0.74rem', color: sumEur > 0 ? '#7c3aed' : '#94a3b8', textAlign: 'right', whiteSpace: 'nowrap' }}>
                            {sumEur > 0 ? `€${sumEur.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : '-'}
                          </Typography>
                        );
                      }

                      return (
                        <TableCell
                          key={`footer_${colId}`}
                          align={isRight ? 'right' : isCenter ? 'center' : 'left'}
                          sx={{
                            backgroundColor: '#f8fafc',
                            py: '4px',
                            px: 'clamp(6px, 0.7vw, 10px)',
                            verticalAlign: 'middle',
                            borderBottom: 'none',
                          }}
                        >
                          {content}
                        </TableCell>
                      );
                    })}
                  </tr>
                </tfoot>
              );
            })()}
          </Table>
        </TableContainer>

        {/* ── Popover Mini Aislado de Detalle por Divisa ── */}
        <TotalesPopover
          ref={totalesPopoverRef}
          totalesRef={breakdownRef}
        />


        <TablePagination
          component="div"
          count={preparedRows.length}
          page={page}
          onPageChange={(_, newPage) => {
            setPage(newPage);
            setRenderLimit(RENDER_CHUNK);
          }}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => {
            const nextVal = parseInt(e.target.value, 10);
            setRowsPerPage(nextVal);
            setPage(0);
            setRenderLimit(RENDER_CHUNK);
            savePreferences(visibleCols, columnOrder, nextVal);
          }}
          rowsPerPageOptions={[
            25,
            50,
            100,
            250,
            500,
            { value: -1, label: 'Todas' }
          ]}
          labelRowsPerPage="Filas por página:"
          labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
          sx={{
            borderTop: '1px solid #eef2f7',
            bgcolor: '#ffffff',
            position: 'sticky',
            bottom: 0,
            zIndex: 10,
            mb: { xs: '52px', md: 0 },
            '& .MuiTablePagination-toolbar': { minHeight: 'clamp(32px, 2.5vw, 40px)' },
            '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
              fontSize: 'clamp(0.60rem, 0.56rem + 0.05vw, 0.68rem)'
            }
          }}
        />
      </Paper>

      {/* ── FOOTER FLOTANTE TOTALES EN MÓVIL ── */}
      <ReporteMobileTotalsFooter
        preparedRows={preparedRows}
        selectedRows={selectedRows}
        selectedCount={selectedCount}
        onGenerarExcel={generarExcel}
        isReadyTab={isReadyTab}
        onSelectAll={handleToggleSelectAll}
        allSelected={allReadySelected}
      />

      {/* ── MODAL: PERSONALIZACIÓN DE COLUMNAS VISIBLES (CON PERSISTENCIA) ── */}
      <Dialog
        open={columnasModalOpen}
        onClose={() => setColumnasModalOpen(false)}
        maxWidth="xs"
        fullWidth
        disableRestoreFocus
        PaperProps={{
          sx: {
            borderRadius: '16px',
            p: 1,
            boxShadow: '0 20px 25px -5px rgba(15, 23, 42, 0.15)',
          }
        }}
      >
        <DialogTitle sx={{ pb: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <ViewColumnIcon sx={{ color: '#0655A5' }} />
            <Typography sx={{ fontWeight: 800, fontSize: 16, color: '#0f172a' }}>
              Personalizar Columnas
            </Typography>
          </Box>
          <IconButton size="small" onClick={() => setColumnasModalOpen(false)}>
            <CloseIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </DialogTitle>
        <Divider />
        <DialogContent sx={{ py: 2, maxHeight: '60vh', overflowY: 'auto' }}>
          <Typography sx={{ fontSize: 12, color: '#64748b', mb: 2, fontWeight: 500 }}>
            Reordena o activa/desactiva las columnas que deseas visualizar. Tus preferencias se guardarán automáticamente.
          </Typography>
          <Stack spacing={1}>
            {columnOrder.map((colId, index) => {
              const col = ALL_COLUMNS_DEF.find(c => c.id === colId);
              if (!col) return null;
              const isVisible = visibleCols[colId] !== false;

              return (
                <Box
                  key={colId}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    p: '6px 10px',
                    borderRadius: '10px',
                    bgcolor: isVisible ? '#f0f7ff' : '#f8fafc',
                    border: `1px solid ${isVisible ? '#bfdbfe' : '#e2e8f0'}`,
                    transition: 'all 0.15s ease',
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', mr: 0.5 }}>
                      <IconButton
                        size="small"
                        disabled={index === 0}
                        onClick={() => moveColumn(index, -1)}
                        sx={{ p: 0.2, color: index === 0 ? '#cbd5e1' : '#64748b' }}
                      >
                        <ExpandLessIcon sx={{ fontSize: 16 }} />
                      </IconButton>
                      <IconButton
                        size="small"
                        disabled={index === columnOrder.length - 1}
                        onClick={() => moveColumn(index, 1)}
                        sx={{ p: 0.2, color: index === columnOrder.length - 1 ? '#cbd5e1' : '#64748b' }}
                      >
                        <ExpandMoreIcon sx={{ fontSize: 16 }} />
                      </IconButton>
                    </Box>
                    <Typography sx={{ fontSize: 13, fontWeight: 700, color: isVisible ? '#0655A5' : '#64748b' }}>
                      {col.label}
                    </Typography>
                  </Box>
                  <Switch
                    size="small"
                    checked={isVisible}
                    onChange={() => toggleColumn(col.id)}
                    disabled={col.alwaysVisible}
                    color="primary"
                  />
                </Box>
              );
            })}
          </Stack>
        </DialogContent>
        <Divider />
        <DialogActions sx={{ p: 2, justifyContent: 'space-between' }}>
          <Button
            size="small"
            variant="text"
            color="inherit"
            onClick={resetColumns}
            sx={{ textTransform: 'none', fontSize: 12, fontWeight: 700, color: '#64748b' }}
          >
            Restaurar predeterminadas
          </Button>
          <Button
            size="small"
            variant="contained"
            onClick={() => setColumnasModalOpen(false)}
            sx={{
              textTransform: 'none',
              fontSize: 12,
              fontWeight: 800,
              bgcolor: '#0655A5',
              borderRadius: '8px',
              px: 2,
              boxShadow: 'none',
              '&:hover': { bgcolor: '#054785', boxShadow: 'none' }
            }}
          >
            Listo
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
